/**
 * Fluxo de triagem — o editor, nos dois degraus da spec 12.
 *
 * ## Salvar não é publicar, e o cabeçalho diz o tempo todo
 *
 * §3.1: quem edita mexe num **rascunho**; o que atende cliente é a versão **publicada**. A versão anterior
 * desta tela salvava direto no que atendia — cada tecla digitada já estava respondendo cliente de verdade.
 *
 * ## Dois degraus sobre UM modelo
 *
 * §4: `Simples` é um formulário de quatro campos, sem conceito de passo, e é o que a recepcionista usa.
 * `Detalhado` é a lista de passos. **Não são dois fluxos** — é o mesmo `passos[]` visto de duas maneiras
 * (regra 3 do §4.5), e por isso o botão de descer some quando o fluxo não cabe, **com o motivo**, em vez de
 * mostrar o fluxo truncado.
 *
 * ## O contador de problemas leva ao passo
 *
 * §3.2: contador que só conta é contador que ensina a ignorar. Clicar rola até o passo e o destaca.
 *
 * ## O que esta tela ainda NÃO é
 *
 * **Canvas** (degrau 3, §5) e **copiloto** (§7). Os dois estão na ordem do §10 depois destes, e o motivo de
 * não estarem aqui é o mesmo da spec: canvas sobre modelo sem limite produz fluxo que ninguém consegue
 * consertar — e o teto, a versão e a trilha só passaram a existir agora.
 */

import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Botao, EstadoCarregando, EstadoDaFalha, Grade, falhaDe, identificadorLegivel, Ladrilho, type TomDoLadrilho } from "@hugi/ui";
import { Abas, CabecalhoDaTela, PaginaInteira, Selo } from "@hugi/ui/kit";
import "./automacoes.css";
import { Blocks, Building2, Camera, GitBranch, Globe, ListOrdered, MessageCircleQuestion, MessageSquare, RefreshCw, Scale, Scissors, ShoppingBag, Sparkles, Split, Square, Stethoscope, Timer, TrendingUp, Zap } from "lucide-react";
import {
  arquivarFluxo,
  descartarRascunho,
  publicarFluxo,
  salvarFluxo,
  useFluxo,
  useModelos,
  enderecoDeExportacao,
  importarModelo,
  type ResultadoDaImportacao,
  useAgentes,
  useCatalogos,
  useConexoes,
  useMotivosDePerda,
  type FormaSimples,
  type PassoCompleto,
  type PassoDoFluxo,
  type RespostaDoFluxo,
  type ResumoDoFluxo,
  type TipoDeGatilho,
  type TipoDePasso,
  useEtapasDeTodosOsQuadros,
} from "./dados.js";
/**
 * As regras vêm do módulo PURO do servidor — não são cópias.
 *
 * `src/canal/fluxo.ts` não importa nada e não toca banco, e é por isso que ele roda no navegador. É a única
 * forma de a validação da tela e a do servidor recusarem exatamente as mesmas coisas, e de o simulador do
 * §3.4 executar a MESMA `proximoPasso` que atende cliente.
 */
import {
  daEntrevista,
  daFormaSimples,
  desenhar,
  paraFormaSimples,
  problemasDoFluxo,
  proximoPasso,
  usaBlocos,
  GATILHOS_SO_COM_BLOCOS,
  gatilhoCombinaComAForma,
  avaliarBloco,
  type Fluxo as FluxoModelo,
} from "../../../src/canal/fluxo.js";

/**
 * Os gatilhos (§3.1 da spec 11).
 *
 * Quais servem a um fluxo de menu é decidido por **`gatilhoCombinaComAForma`**, no domínio — a mesma
 * função que `publicarFluxo` usa como portão. Até 05/09 a regra era um `soComBlocos` escrito NESTA
 * lista, e por isso ela era a única guarda: bastava escolher o gatilho com blocos e apagar os blocos
 * depois para publicar um par que o motor não honra.
 *
 * Os incompatíveis aparecem desabilitados **com o motivo escrito** em vez de simplesmente não
 * aparecerem: o cliente que leu a spec ou viu no concorrente vai procurar, e uma opção ausente sem
 * explicação é indistinguível de uma opção que não existe.
 */
export const QUANDO: readonly {
  id: TipoDeGatilho;
  rotulo: string;
  frase: string;
  /** O rótulo do campo de parâmetro, quando o gatilho tem um. */
  valor?: "texto" | "etiqueta" | "motivo" | "etapa";
}[] = [
  { id: "nunca", rotulo: "Desligado", frase: "Nenhum contato vê o fluxo." },
  {
    id: "primeira_mensagem",
    rotulo: "Na primeira mensagem",
    frase: "Aparece quando a conversa começa, e não atrapalha depois.",
  },
  {
    id: "fora_do_horario",
    rotulo: "Só fora do horário",
    frase: "Dentro do horário quem responde é a sua equipe.",
  },
  {
    id: "mensagem_contendo",
    rotulo: "Quando a mensagem contém",
    frase: "Vale em qualquer momento da conversa, não só na primeira mensagem.",
    valor: "texto",
  },
  {
    id: "etiqueta_aplicada",
    rotulo: "Quando a equipe aplica uma etiqueta",
    frase: "Só alcança contato com conversa aberta — o Hugi não começa conversa por conta própria.",
    valor: "etiqueta",
  },
  {
    id: "motivo_de_encerramento",
    rotulo: "Quando o atendimento é finalizado com um motivo",
    frase: "Serve para tentar reagendar quem não compareceu.",
    valor: "motivo",
  },
  {
    id: "oportunidade_movida",
    rotulo: "Quando uma oportunidade entra numa etapa do funil",
    frase: "Movida, criada nela, ganha ou perdida. Só alcança contato com conversa aberta — o Hugi não começa conversa por conta própria.",
    valor: "etapa",
  },
];

type Degrau = "simples" | "detalhado" | "desenho";

/**
 * As ABAS da automação (05/09).
 *
 * A ordem é a de MONTAR: o que ela faz → quando ela roda → testar → publicar → o que aconteceu.
 * Não é a ordem em que os blocos foram construídos, que era a da rolagem que o owner recusou.
 *
 * ⚠️ **"Quando ela roda" vem DEPOIS de "Os passos", e isso é o contrário do assistente de criação.**
 * Lá o gatilho vem primeiro porque ele decide quais formas existem; aqui a forma já foi escolhida, e
 * quem abre uma automação existente vem mexer no que ela FAZ — o gatilho já está resolvido.
 */
type AbaDaAutomacao = "passos" | "quando" | "testar" | "publicar" | "atividade";
const ABAS_DA_AUTOMACAO: readonly { id: AbaDaAutomacao; rotulo: string }[] = [
  { id: "passos", rotulo: "Os passos" },
  { id: "quando", rotulo: "Quando ela roda" },
  { id: "testar", rotulo: "Testar" },
  { id: "publicar", rotulo: "Publicar" },
  { id: "atividade", rotulo: "Atividade" },
];

/** Por onde a pessoa começa quando não há nada. §4 e §7 da spec 12. */
type Comeco = "escolher" | "entrevista";

export const ROTULO_DO_GATILHO: Readonly<Record<string, string>> = {
  nunca: "Sem gatilho",
  primeira_mensagem: "Primeira mensagem",
  fora_do_horario: "Fora do horário",
  mensagem_contendo: "Mensagem contendo",
  etiqueta_aplicada: "Etiqueta aplicada",
  motivo_de_encerramento: "Motivo de encerramento",
  oportunidade_movida: "Etapa do funil",
};

/*
  A LISTA DE FLUXOS MORAVA AQUI, e foi APAGADA em 05/09.

  Ela virou a tela de **Automações** (`Automacoes.tsx`), que mostra as automações e os assistentes
  juntos. Deixá-la aqui compilando e sem chamador seria uma segunda tela para a mesma coisa,
  divergindo em silêncio da que a substituiu — e a próxima sessão a encontraria intacta, com todo o
  ar de estar viva. O que sobrevive deste arquivo é o EDITOR (`EditorDeFluxo`) e a biblioteca de
  modelos, os dois usados pela área nova.
*/

/**
 * O SETOR do modelo, com cara.
 *
 * ## Por que ele existe
 *
 * A lista eram oito retângulos iguais de largura inteira com uma linha de texto dentro — sem
 * ladrilho, sem cor, sem hierarquia. O owner viu em produção e disse o que era: *"bem feio"*. Ela
 * era o painel lateral da frente de modelos, promovido a tela inteira sem redesenho, e por isso a
 * única tela de Automações que não usava a linguagem do resto do painel.
 *
 * ## O mapa é de APRESENTAÇÃO, e o desconhecido cai em pé
 *
 * `setor` é texto livre que vem do servidor, então este mapa **não pode** ser a única forma de um
 * modelo aparecer: setor novo (ou vindo de um arquivo de terceiro) cai no `neutro` com o ícone
 * genérico, e o cartão sai inteiro. Marcar por `id` do modelo seria pior — cada modelo novo nasceria
 * sem ladrilho até alguém lembrar de acrescentá-lo aqui.
 */
const MARCA_DO_SETOR: Readonly<Record<string, { readonly tom: TomDoLadrilho; readonly icone: React.ReactNode }>> = {
  Fotografia: { tom: "ct", icone: <Camera size={18} /> },
  "Saúde": { tom: "wa", icone: <Stethoscope size={18} /> },
  Beleza: { tom: "ia", icone: <Scissors size={18} /> },
  "Imobiliário": { tom: "ag", icone: <Building2 size={18} /> },
  "Serviços profissionais": { tom: "eq", icone: <Scale size={18} /> },
  Varejo: { tom: "rl", icone: <ShoppingBag size={18} /> },
  "Vendas (qualquer setor)": { tom: "fx", icone: <TrendingUp size={18} /> },
  __desconhecido: { tom: "neutro", icone: <Blocks size={18} /> },
};

/**
 * A BIBLIOTECA DE MODELOS.
 *
 * O que ela mostra por modelo é o que decide se alguém clica: o SETOR, o que ele faz, e se traz
 * assistente junto. E a linha que ninguém quer descobrir depois de publicar — **o assistente chega
 * sem base de conhecimento** — está na tela, não num aviso de rodapé.
 *
 * Importar leva direto ao fluxo criado: o modelo é ponto de partida, e o passo seguinte é ler o que
 * veio. Ele entra DESLIGADO, e é a própria tela do fluxo que pede o gatilho e o Publicar.
 */
/** O gatilho que o modelo sugere (contrato C4 da spec 23). Ausente no servidor antigo: a tela segue sem ele. */
export type GatilhoSugerido = { readonly quando: TipoDeGatilho; readonly valor?: string } | null;
const gatilhoDe = (x: unknown): GatilhoSugerido => {
  const g = (x as { gatilhoSugerido?: { quando?: unknown; valor?: unknown } | null } | null)?.gatilhoSugerido;
  if (!g || typeof g.quando !== "string") return null;
  return { quando: g.quando as TipoDeGatilho, ...(typeof g.valor === "string" && g.valor ? { valor: g.valor } : {}) };
};

export function Biblioteca(props: {
  /** `gatilho`: o sugerido pelo modelo (C4), para o editor pré-selecionar — nunca ligar. */
  readonly aoImportar: (fluxoId: string, gatilho?: GatilhoSugerido) => void;
  /** Quando o modelo traz só assistente, é ele que o relatório tem para oferecer. */
  readonly aoAbrirAssistente?: (agenteId: string) => void;
  readonly aoFechar: () => void;
}) {
  const cliente = useQueryClient();
  const q = useModelos();
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [relatorio, setRelatorio] = useState<ResultadoDaImportacao | null>(null);
  const [colado, setColado] = useState("");
  const [aba, setAba] = useState<"nossos" | "arquivo">("nossos");
  // Os nomes no DESTINO. Ver o campo na aba do arquivo: sem eles, um arquivo cujo nome já existe
  // nesta empresa vira um beco — a rota recusa, corretamente, e a tela não tem o que oferecer.
  const [nomeDoFluxo, setNomeDoFluxo] = useState("");
  const [nomeDoAgente, setNomeDoAgente] = useState("");

  const usar = async (id: string) => {
    setOcupado(id);
    setErro(null);
    const r = await importarModelo(cliente, { deFabrica: id });
    setOcupado(null);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível importar.");
      return;
    }
    // O relatório fica na tela ANTES de navegar: é onde está o que a importação criou e o que ficou
    // pendente, e quem for levado direto ao editor nunca leria.
    setRelatorio(r.dados ?? {});
  };

  /*
    IMPORTAR UM ARQUIVO — a metade que faltava do "mandar modelos para os meus clientes".

    Exportar já devolvia o JSON; importar só aceitava modelo NOSSO. Quem recebia o arquivo de um
    parceiro tinha de chamar a rota por script. A rota sempre aceitou `modelo` no corpo — o que
    faltava era o campo.

    ## O JSON é lido AQUI antes de sair, e isso não é validação duplicada

    O servidor valida o formato de novo, e é ele quem manda. O que esta leitura evita é outra coisa:
    mandar 200 kB de texto que não é JSON e receber de volta um erro de campo, quando o problema é
    que a pessoa colou o arquivo errado. `JSON.parse` responde isso na hora, sem ida à rede.
  */
  const importarArquivo = async (texto: string) => {
    setErro(null);
    let modelo: unknown;
    try {
      modelo = JSON.parse(texto);
    } catch {
      setErro("Isto não é um arquivo de modelo — o conteúdo não é JSON válido.");
      return;
    }
    setOcupado("arquivo");
    const r = await importarModelo(cliente, {
      modelo,
      ...(nomeDoFluxo.trim() ? { nomeDoFluxo: nomeDoFluxo.trim() } : {}),
      ...(nomeDoAgente.trim() ? { nomeDoAgente: nomeDoAgente.trim() } : {}),
    });
    setOcupado(null);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível importar este arquivo.");
      return;
    }
    setColado("");
    setRelatorio(r.dados ?? {});
  };

  if (relatorio) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium">Modelo importado</p>
        {(relatorio.criados ?? []).length > 0 ? (
          <div>
            <p className="text-xs font-medium">Criei o que faltava nesta empresa</p>
            <ul className="mt-1 list-inside list-disc text-xs text-[color:var(--tinta-fraca)]">
              {(relatorio.criados ?? []).map((c) => (
                <li key={`${c.tipo}:${c.nome}`}>
                  {c.tipo} <strong>{c.nome}</strong>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {(relatorio.criados ?? []).length === 0 && (relatorio.pendencias ?? []).length === 0 ? (
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            Nada precisou ser criado nesta empresa: fila, etiqueta e motivo do arquivo já existiam
            aqui, com o mesmo nome.
          </p>
        ) : null}
        {(relatorio.pendencias ?? []).length > 0 ? (
          <div>
            <p className="text-xs font-medium">Falta você decidir</p>
            <ul className="mt-1 list-inside list-disc text-xs text-[color:var(--tinta-fraca)]">
              {(relatorio.pendencias ?? []).map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {relatorio.fluxoId ? (
            <Botao tipo="primario" onClick={() => props.aoImportar(relatorio.fluxoId!, gatilhoDe(relatorio))}>
              Abrir a automação
            </Botao>
          ) : null}
          {/* Modelo só de assistente não tem fluxo para abrir, e o relatório ficava sem saída
              nenhuma — três botões de fechar e nada para ver. */}
          {!relatorio.fluxoId && relatorio.agenteId && props.aoAbrirAssistente ? (
            <Botao tipo="primario" onClick={() => props.aoAbrirAssistente!(relatorio.agenteId!)}>
              Abrir o assistente
            </Botao>
          ) : null}
          {/* Achado abrindo a tela: o painel já está aberto, então clicar em "Usar um modelo" de
              novo NÃO troca o estado e não remonta nada — a pessoa continuava vendo o relatório da
              importação anterior e concluía que o botão tinha parado de funcionar. A volta é daqui. */}
          <Botao tipo="secundario" onClick={() => setRelatorio(null)}>
            Ver outros modelos
          </Botao>
          <Botao tipo="secundario" onClick={props.aoFechar}>
            Fechar
          </Botao>
        </div>
      </div>
    );
  }

  if (q.isPending) return <EstadoCarregando rotulo="Carregando os modelos" />;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;

  return (
    <div className="flex flex-col gap-3">
      {/*
        ⚠️ AS DUAS ABAS PRECISAM PARECER UM PAR, e a `Aba` dos degraus do fluxo não serve aqui.

        Ela pinta a ativa de `--acao-forte` cheio e deixa a inativa como TEXTO SOLTO — o que funciona
        numa fileira de três com contexto em volta, e não funciona num par: a primeira lê como botão
        de ação e a segunda como legenda. Foi o que o owner viu na captura de produção. Este é o
        estilo de abas do assistente, com `role="tablist"`: as duas têm caixa, e o que muda entre
        elas é o tom.
      */}
      <div role="tablist" aria-label="De onde vem o modelo" className="flex flex-wrap gap-1">
        {([
          { id: "nossos", rotulo: "Modelos do Hugi" },
          { id: "arquivo", rotulo: "Um arquivo que eu recebi" },
        ] as const).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={aba === t.id}
            onClick={() => setAba(t.id)}
            className={[
              "rounded-[11px] px-3 py-1.5 text-sm transition-colors",
              aba === t.id
                ? "font-semibold text-[color:var(--acao-texto)]"
                : "text-[color:var(--tinta-fraca)] hover:bg-[color:var(--vidro-controle)]",
            ].join(" ")}
            style={aba === t.id ? { background: "var(--acao-clara)" } : undefined}
          >
            {t.rotulo}
          </button>
        ))}
      </div>
      {erro ? (
        <p role="alert" className="text-sm" style={{ color: "var(--erro-texto)" }}>
          {erro}
        </p>
      ) : null}
      {aba === "arquivo" ? (
        <div className="flex flex-col gap-3">
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            Um arquivo exportado do Hugi — por você, ou por quem montou a automação. Ele entra{" "}
            <strong>desligado</strong>: nada dele começa a atender antes de você publicar.
          </p>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Escolher o arquivo</span>
            <input
              type="file"
              accept="application/json,.json"
              className="text-xs"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                void f.text().then((t) => {
                  setColado(t);
                  void importarArquivo(t);
                });
                // Sem isto, escolher o MESMO arquivo de novo não dispara o evento — e a pessoa
                // conclui que o campo parou de funcionar depois de um erro.
                e.target.value = "";
              }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Ou cole o conteúdo dele aqui</span>
            <textarea
              className="campo font-mono text-xs"
              rows={6}
              value={colado}
              onChange={(e) => setColado(e.target.value)}
              placeholder={'{ "formato": 1, "fluxo": { … } }'}
            />
          </label>
          {/*
            OS NOMES NO DESTINO, e eles não são conveniência.

            Nome de fluxo e de assistente é ÚNICO por empresa — a rota recusa o repetido, e está
            certa: dois assistentes com o mesmo nome são indistinguíveis no passo do fluxo. Sem estes
            dois campos, receber um arquivo de um parceiro que usa os mesmos nomes que você é um beco:
            a recusa aparece e não há o que fazer na tela. Vazio = o nome que vem no arquivo.
          */}
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-1 flex-col gap-1" style={{ minWidth: "12rem" }}>
              <span className="text-xs font-medium">Nome da automação aqui (opcional)</span>
              <input className="campo" value={nomeDoFluxo} onChange={(e) => setNomeDoFluxo(e.target.value)} placeholder="o nome que vem no arquivo" />
            </label>
            <label className="flex flex-1 flex-col gap-1" style={{ minWidth: "12rem" }}>
              <span className="text-xs font-medium">Nome do assistente aqui (opcional)</span>
              <input className="campo" value={nomeDoAgente} onChange={(e) => setNomeDoAgente(e.target.value)} placeholder="o nome que vem no arquivo" />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Botao
              tipo="primario"
              onClick={() => void importarArquivo(colado)}
              desabilitado={ocupado !== null || colado.trim() === ""}
              motivoDeDesabilitar={ocupado ? "Importando…" : "Cole o conteúdo do arquivo."}
            >
              {ocupado === "arquivo" ? "Importando…" : "Importar este arquivo"}
            </Botao>
          </div>
          {/*
            ⚠️ A RESSALVA QUE PRECISA ESTAR NA TELA: um arquivo de assistente carrega a BASE DE
            CONHECIMENTO de quem o exportou. Quem importa está trazendo conteúdo de outra empresa
            para dentro da sua — e o assistente cita a base como verdade.
          */}
          <p className="text-xs text-[color:var(--atencao-texto)]">
            Se o arquivo tiver um assistente, ele pode trazer a base de conhecimento de quem o
            exportou. Confira o que ele sabe antes de publicar.
          </p>
        </div>
      ) : (
      <Grade minimo={320}>
        {q.data.modelos.map((m) => {
          const marca = MARCA_DO_SETOR[m.setor] ?? MARCA_DO_SETOR["__desconhecido"]!;
          return (
            /*
              ⚠️ BORDA, e não uma camada de `vidro`: medido no escuro, a camada a mais sob o botão
              secundário levava o rótulo dele de 4,95 para 4,05 (alvo 4,5). Vidro sobre vidro sobre
              vidro clareia o fundo, e o texto de ação é claro — as duas coisas andam para o mesmo
              lado. O que dá corpo ao cartão é o ladrilho, não mais uma camada.
            */
            <div
              key={m.id}
              className="flex flex-col gap-2.5 rounded-xl border border-[color:var(--borda)] p-3.5 transition-colors hover:border-[color:var(--borda-forte)]"
            >
              <div className="flex items-start gap-2.5">
                <Ladrilho tom={marca.tom}>{marca.icone}</Ladrilho>
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-tight">{m.nome}</p>
                  <p className="mt-0.5 text-xs text-[color:var(--tinta-fraca)]">
                    {m.setor}
                    {m.temFluxo ? ` · ${m.passos} passos` : ""}
                    {m.temAgente ? " · com assistente" : ""}
                  </p>
                  {gatilhoDe(m) ? (
                    <p className="mt-0.5 text-xs text-[color:var(--tinta-fraca)]">
                      {ROTULO_DO_GATILHO[gatilhoDe(m)!.quando] ?? gatilhoDe(m)!.quando}
                      {gatilhoDe(m)!.valor && gatilhoDe(m)!.quando === "mensagem_contendo" ? ` "${gatilhoDe(m)!.valor}"` : ""}
                    </p>
                  ) : null}
                </div>
              </div>
              <p className="text-xs leading-relaxed text-[color:var(--tinta-fraca)]">{m.descricao}</p>
              <div className="mt-auto pt-0.5">
                <Botao tipo="secundario" onClick={() => void usar(m.id)} desabilitado={ocupado !== null}>
                  {ocupado === m.id ? "Importando…" : "Usar este modelo"}
                </Botao>
              </div>
            </div>
          );
        })}
      </Grade>
      )}
    </div>
  );
}

/**
 * O SELO do cabeçalho da automação: o que está NO AR, e nunca o que está no formulário.
 *
 * Ele lia o `quando` do formulário, então trocar o rádio já mudava o selo para "Atendendo" antes de
 * qualquer publicação. Com o assistente de criação pré-selecionando o gatilho, uma automação
 * recém-criada — sem um passo escrito — abria dizendo que estava **atendendo**. Achado abrindo a
 * tela em 05/09; a versão antiga do defeito exigia um clique para aparecer, e por isso passou.
 *
 * Cartão que mente sobre o próprio estado é pior que cartão nenhum: ele encerra a investigação de
 * quem foi ali descobrir por que nada acontece. Mesma correção do cartão de Canais em 02/09.
 */
export function seloDaAutomacao(
  noAr: TipoDeGatilho,
  versao: number | null,
  escolhidoNoFormulario: TipoDeGatilho,
): { readonly rotulo: string; readonly estado: "ok" | "neutro"; readonly detalhe?: string } {
  const pendente =
    escolhidoNoFormulario !== noAr
      ? { detalhe: "O gatilho escolhido só vale a partir da próxima publicação." }
      : {};
  if (versao === null) return { rotulo: "Nunca publicada", estado: "neutro", ...pendente };
  if (noAr === "nunca") return { rotulo: "Desligada", estado: "neutro", ...pendente };
  return { rotulo: "Atendendo", estado: "ok", ...pendente };
}

/**
 * O editor de UMA automação — o que era a tela inteira até a 0061.
 *
 * ## `gatilhoInicial`, e por que ele não é gravado na criação
 *
 * O assistente de "Nova automação" pergunta **quando** antes de qualquer outra coisa, porque foi
 * essa a pergunta que faltava (o owner montou uma automação e não sabia quando ela rodaria). Mas o
 * gatilho só entra no banco ao **publicar** (`publicarFluxo`), e isso não mudou: gravá-lo na criação
 * poria a automação para atender cliente antes de ela ter um passo. Então o assistente passa a
 * escolha por aqui, ela vira o valor pré-selecionado do painel de publicar, e a automação continua
 * nascendo **desligada**.
 */
export function EditorDeFluxo(props: {
  readonly fluxoId: string;
  readonly aoVoltar: () => void;
  readonly gatilhoInicial?: { readonly quando: TipoDeGatilho; readonly valor: string } | null;
}) {
  const cliente = useQueryClient();
  const q = useFluxo(props.fluxoId);

  const [aba, setAba] = useState<AbaDaAutomacao>("passos");
  const [degrau, setDegrau] = useState<Degrau>("simples");
  const [passos, setPassos] = useState<PassoCompleto[]>([]);
  const [simples, setSimples] = useState<FormaSimples | null>(null);
  const [quando, setQuando] = useState<TipoDeGatilho>("nunca");
  const [quandoValor, setQuandoValor] = useState("");
  const [repeticoes, setRepeticoes] = useState(2);
  const [filaDesistencia, setFilaDesistencia] = useState("");
  const [nota, setNota] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [destacado, setDestacado] = useState<string | null>(null);
  const [comeco, setComeco] = useState<Comeco>("escolher");
  /*
    A AUTOMAÇÃO RECÉM-CRIADA NÃO ABRE COM ERRO (spec 23, jornada 2). O modelo e a semente nascem com filas em branco
    e frases a escrever, e a lista de problemas em vermelho era a primeira coisa que a pessoa lia — antes de ter
    mexido em nada. Os problemas aparecem quando ela mexe, quando tenta ligar, ou quando a automação já foi
    publicada alguma vez.
  */
  const [mexeu, setMexeu] = useState(false);
  const [tentouLigar, setTentouLigar] = useState(false);
  const conexoes = useConexoes();
  const mudarPassos = (p: PassoCompleto[]) => {
    setMexeu(true);
    setPassos(p);
  };
  const mudarSimples = (f: FormaSimples) => {
    setMexeu(true);
    setSimples(f);
  };

  const dados = q.data;
  useEffect(() => {
    if (!dados) return;
    setPassos(dados.emEdicao.map((p) => ({ ...p, opcoes: [...p.opcoes] })));
    setSimples(dados.simples ? { ...dados.simples, opcoes: [...dados.simples.opcoes] } : null);
    /*
      O gatilho inicial só vale para automação que NUNCA foi publicada. Numa que já atende, ele
      sobrescreveria a escolha que está no ar com a de um assistente que a pessoa usou meses atrás —
      e o painel de publicar mostraria um gatilho que não é o dela.
    */
    const nuncaPublicada = (dados.fluxo?.versao ?? null) === null;
    setQuando(nuncaPublicada && props.gatilhoInicial ? props.gatilhoInicial.quando : (dados.fluxo?.quando ?? "nunca"));
    setQuandoValor(nuncaPublicada && props.gatilhoInicial ? props.gatilhoInicial.valor : (dados.fluxo?.quandoValor ?? ""));
    setRepeticoes(dados.fluxo?.passosSemResposta ?? 2);
    setFilaDesistencia(dados.fluxo?.filaDeDesistenciaId ?? "");
    // Abre no degrau que couber. Quem tem fluxo com ramo não deve ver a tela simples primeiro e descobrir
    // depois que ela não mostra o fluxo dele.
    setDegrau(dados.simples ? "simples" : "detalhado");
  }, [dados, props.gatilhoInicial]);

  /*
    Os problemas são recalculados AQUI, sobre o que está na tela — e não os que vieram do servidor.
    Os do servidor são do que está gravado; a pessoa está editando. Mostrar os gravados faria a contagem
    ficar parada enquanto ela corrige, que é o jeito mais rápido de a validação perder credibilidade.

    A função é a MESMA do servidor (`src/canal/fluxo.ts`, puro). Não é uma segunda regra.

    **Antes dos `return` antecipados, e isso não é estilo.** `useMemo` depois deles é violação da ordem dos
    hooks: no primeiro render (carregando) ele não é chamado, no segundo é, e o React derruba a tela inteira
    com "Rendered more hooks than during the previous render". Foi exatamente o que aconteceu — a tela do
    fluxo abriu **em branco**, e apareceu ao capturá-la, não em typecheck nem em teste.
  */
  const paraValidar = useMemo(
    () => (degrau === "simples" && simples ? passosDoSimples(simples) : passos),
    [degrau, simples, passos],
  );
  const problemas = useMemo(() => problemasDoFluxo(paraValidar), [paraValidar]);

  if (q.isPending) return <EstadoCarregando rotulo="Carregando o fluxo" />;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;
  if (!dados) return null;

  const { filas, etiquetas, uso, versoes } = dados;
  const proativo = dados.proativo ?? {
    podeAbordar: null,
    porQueNaoAborda: null,
    contatosComOptin: 0,
    contatosSemOptin: 0,
    categoriasDeTemplate: [],
  };
  const bloqueantes = problemas.filter((p) => p.bloqueante);
  const avisos = problemas.filter((p) => !p.bloqueante);
  const temRascunho = dados.fluxo?.rascunho !== null && dados.fluxo?.rascunho !== undefined;
  const nuncaPublicada = (dados.fluxo?.versao ?? null) === null;
  const mostrarProblemas = !nuncaPublicada || mexeu || tentouLigar;
  /** O formulário difere do que está gravado (rascunho, ou o publicado quando não há rascunho). */
  const sujo =
    JSON.stringify({ p: dados.emEdicao, s: dados.simples, r: dados.fluxo?.passosSemResposta ?? 2, f: dados.fluxo?.filaDeDesistenciaId ?? "" }) !==
    JSON.stringify({ p: passos, s: simples, r: repeticoes, f: filaDesistencia });
  const noAr = !nuncaPublicada && (dados.fluxo?.quando ?? "nunca") !== "nunca";
  const gatilhoMudou = quando !== (dados.fluxo?.quando ?? "nunca") || (quandoValor.trim() || null) !== (dados.fluxo?.quandoValor ?? null);
  const corpoDoRascunho = () =>
    degrau === "simples" && simples
      ? { simples, passosSemResposta: repeticoes, filaDeDesistenciaId: filaDesistencia || null }
      : { passos, passosSemResposta: repeticoes, filaDeDesistenciaId: filaDesistencia || null };

  const salvar = async () => {
    setOcupado(true);
    setErro(null);
    setAviso(null);
    const r = await salvarFluxo(cliente, props.fluxoId, corpoDoRascunho());
    setOcupado(false);
    if (r.ok) {
      setAviso("Rascunho salvo. Ele ainda não atende ninguém — publique quando estiver pronto.");
      return;
    }
    setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar.");
  };

  /*
    LIGAR / PUBLICAR ALTERAÇÕES — o primário do cabeçalho (spec 23). Publicar exige rascunho no servidor, então
    quem só trocou o gatilho (ou está desligada sem rascunho) salva antes. Se a publicação falhar depois de salvar,
    a frase diz o que ficou feito.
  */
  const ligar = async () => {
    setTentouLigar(true);
    setErro(null);
    setAviso(null);
    const gatilho = QUANDO.find((g) => g.id === quando);
    if (quando === "nunca") {
      setAba("quando");
      setErro("Escolha quando ela roda.");
      return;
    }
    if (gatilho?.valor && !quandoValor.trim()) {
      setAba("quando");
      setErro(gatilho.valor === "texto" ? "Diga qual palavra dispara a automação." : "Escolha o que dispara a automação.");
      return;
    }
    if (bloqueantes.length > 0) {
      setErro(`Falta resolver: ${bloqueantes[0]!.texto}`);
      return;
    }
    setOcupado(true);
    let salvou = false;
    if (sujo || !temRascunho) {
      const s = await salvarFluxo(cliente, props.fluxoId, corpoDoRascunho());
      if (!s.ok) {
        setOcupado(false);
        setErro(s.falha?.erro.mensagem ?? "Não foi possível salvar.");
        return;
      }
      salvou = true;
    }
    const r = await publicarFluxo(cliente, props.fluxoId, { quando, quandoValor: quandoValor.trim() || null, nota: nota.trim() || null });
    setOcupado(false);
    if (r.ok) {
      setNota("");
      setAviso(noAr ? "Alterações publicadas." : `Ligada: roda ${(gatilho?.rotulo ?? "").toLowerCase()}.`);
      return;
    }
    const motivo = r.falha?.erro.mensagem ?? "o servidor não respondeu";
    setErro(salvou ? `Rascunho salvo, mas não liguei: ${motivo}` : motivo);
  };

  const irAoPasso = (chave: string | null) => {
    if (!chave) return;
    /*
      TROCA DE ABA ANTES DE ROLAR. O contador de problemas fica FORA das abas (ele impede publicar),
      então clicar num problema estando em "Publicar" rolaria para um passo que não está montado — e
      o clique pareceria não fazer nada. É a família do "botão que não faz nada", criada pelas abas.
    */
    setAba("passos");
    setDegrau("detalhado");
    setDestacado(chave);
    // O `setTimeout` espera o degrau trocar antes de procurar o elemento — sem ele, o passo ainda não
    // existe no DOM quando o scroll é pedido.
    setTimeout(() => {
      document.getElementById(`passo-${chave}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 50);
  };

  const selo = seloDaAutomacao(dados.fluxo?.quando ?? "nunca", dados.fluxo?.versao ?? null, quando);
  const situacao = temRascunho
    ? `Rascunho não publicado${dados.fluxo?.rascunhoEm ? ` · salvo em ${new Date(dados.fluxo.rascunhoEm).toLocaleString("pt-BR")}` : ""}`
    : dados.fluxo?.publicadoEm
      ? `Versão ${dados.fluxo.versao} · publicada em ${new Date(dados.fluxo.publicadoEm).toLocaleString("pt-BR")}`
      : "Nunca publicado";

  /*
    MESA DE TRABALHO (spec 22): o cabeçalho com o estado e as ações fica; o contador de problemas e a recusa do
    servidor ficam logo abaixo dele, fora das abas; e as abas ocupam o resto, com o conteúdo rolando DENTRO do
    painel. A página não rola.
  */
  return (
    <PaginaInteira rotulo={dados.fluxo?.nome ?? "Fluxo"} classe="hugi-fluxo">
      {/* --- Cabeçalho: o estado, sempre visível (§3.1) --------------------------------------- */}
      <CabecalhoDaTela
        icone={<GitBranch size={18} />}
        titulo={dados.fluxo?.nome ?? "Fluxo"}
        // A frase segue o que o fluxo É — "um menu" sempre seria o cabeçalho descrevendo outra coisa.
        dica={
          usaBlocos(paraValidar)
            ? "Um fluxo com blocos: pergunta, decide, espera, consulta e chama gente quando precisa."
            : "Um menu que pergunta o assunto e já manda a conversa para a fila certa."
        }
        recorte={
          /*
            ⚠️ O SELO DIZ O QUE ESTÁ NO AR, e não o que está no formulário.

            Ele lia o `quando` do FORMULÁRIO, então trocar o rádio já mudava o selo para "Atendendo"
            antes de qualquer publicação — e com o assistente de criação pré-selecionando o gatilho,
            uma automação recém-criada, sem um passo escrito, abria dizendo que estava atendendo.
            Achado abrindo a tela em 05/09; a versão antiga do defeito exigia um clique para aparecer.

            Cartão que mente sobre o próprio estado é pior que cartão nenhum: ele encerra a
            investigação de quem foi ali descobrir por que nada acontece. Mesma correção do cartão de
            Canais em 02/09.
          */
          <span className="hugi-fluxo-estado">
            <Selo tom={selo.estado === "ok" ? "sucesso" : "neutro"} {...(selo.detalhe ? { dica: selo.detalhe } : {})}>
              {selo.rotulo}
            </Selo>
            <small className={temRascunho ? "is-atencao" : undefined} title={situacao}>
              {situacao}
            </small>
          </span>
        }
        secundarias={
          <>
            {dados.fluxo && quando === "nunca" && !temRascunho ? (
              <Botao
                tipo="perigo"
                onClick={() => {
                  if (window.confirm(`Arquivar o fluxo '${dados.fluxo?.nome ?? ""}'? Ele sai da lista; o histórico fica.`)) {
                    void arquivarFluxo(cliente, props.fluxoId).then((r) => {
                      if (r.ok) props.aoVoltar();
                      else setErro(r.falha?.erro.mensagem ?? "Não foi possível arquivar.");
                    });
                  }
                }}
              >
                Arquivar
              </Botao>
            ) : null}
            {/* Exportar navega para a rota, que devolve o JSON: baixar é do navegador. Leva o
                RASCUNHO quando há um — é o que a pessoa está vendo na tela. */}
            <Botao
              tipo="secundario"
              onClick={() => {
                window.location.href = enderecoDeExportacao({ fluxoId: props.fluxoId });
              }}
            >
              Exportar
            </Botao>
            <Botao tipo="secundario" onClick={props.aoVoltar}>
              Todas as automações
            </Botao>
          </>
        }
        primaria={
          /*
            ⚠️ SALVAR SUBIU PARA O CABEÇALHO quando a tela virou abas (05/09), e não é estética:
            ele morava no cartão de publicar. Com abas, quem está mexendo nos passos e troca de aba
            perderia o que digitou sem nada avisando — e o botão de salvar estaria noutra tela.
            Ele fica PRIMÁRIO enquanto há o que salvar, e secundário quando já está salvo.
          */
          /*
            COM RASCUNHO SALVO, O PRÓXIMO PASSO É PUBLICAR (spec 23, J2): o Salvar descia a secundário e o cabeçalho
            ficava sem ação primária, com o Publicar escondido na 4ª aba.
          */
          /*
            O PRIMÁRIO SEGUE O ESTADO (spec 23): com edição por salvar → Salvar rascunho; salva e fora do ar → Ligar;
            no ar com rascunho ou gatilho novo → Publicar alterações; no ar e em dia → nenhum.
          */
          sujo ? (
            <Botao tipo="primario" onClick={() => void salvar()} desabilitado={ocupado}>
              Salvar rascunho
            </Botao>
          ) : (
            <>
              <Botao tipo="secundario" onClick={() => void salvar()} desabilitado={ocupado}>
                Salvar rascunho
              </Botao>
              {!noAr ? (
                <Botao tipo="primario" onClick={() => void ligar()} desabilitado={ocupado}>
                  {ocupado ? "Ligando…" : "Ligar"}
                </Botao>
              ) : temRascunho || gatilhoMudou ? (
                <Botao tipo="primario" onClick={() => void ligar()} desabilitado={ocupado}>
                  {ocupado ? "Publicando…" : "Publicar alterações"}
                </Botao>
              ) : null}
            </>
          )
        }
      />

      {/*
        ⚠️ DUAS COISAS FICAM FORA DAS ABAS, de propósito (e o Salvar, no cabeçalho, é a terceira):
        o **contador de problemas** (ele impede publicar; escondido numa aba, a pessoa clicaria em
        Publicar sem saber por que o botão está morto) e a **recusa do servidor** (`erro`/`aviso`, que
        não pode ficar na aba em que ninguém está).
      */}
      {/* --- Contador de problemas (§3.2) ----------------------------------------------------- */}
      {mostrarProblemas && problemas.length > 0 && (
        <div className="v2-cartao hugi-fluxo-problemas" role="region" aria-label="Problemas da automação" tabIndex={0}>
          <p>
            {bloqueantes.length > 0 ? (
              <span className="text-[color:var(--erro-texto)]">
                {bloqueantes.length} {bloqueantes.length === 1 ? "problema impede" : "problemas impedem"} publicar
              </span>
            ) : (
              <span className="text-[color:var(--atencao-texto)]">
                {avisos.length} {avisos.length === 1 ? "aviso" : "avisos"}, e nenhum impede publicar
              </span>
            )}
          </p>
          <ul>
            {problemas.map((p, i) => (
              <li key={i} className="text-xs">
                <button
                  type="button"
                  className={
                    "text-left underline " +
                    (p.bloqueante ? "text-[color:var(--erro-texto)]" : "text-[color:var(--tinta-fraca)]")
                  }
                  onClick={() => irAoPasso(p.passo)}
                >
                  {/* "Passo 2", e não a chave interna ("inicio:") — spec 23. */}
                  {p.passo && paraValidar.findIndex((x) => x.chave === p.passo) >= 0
                    ? `Passo ${paraValidar.findIndex((x) => x.chave === p.passo) + 1}: `
                    : ""}
                  {p.texto}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {erro && <p role="alert" className="hugi-fluxo-mensagem is-erro">{erro}</p>}
      {aviso && <p role="status" className="hugi-fluxo-mensagem is-aviso">{aviso}</p>}

      {/*
        --- AS ABAS (05/09) -------------------------------------------------------------------

        Esta tela era UMA ROLAGEM com nove blocos empilhados — passos, gatilho, desistência, teste,
        salvar, publicações, disparo proativo e uso —, e o owner disse o que era: *"tá tudo espalhado
        em uma tela só… não tá bonito, não tá fácil de usar"*. É o mesmo defeito que a tela do
        assistente teve em 03/09, e a resposta que ele deu na época foi a mesma: abas.

        A ordem é a de MONTAR uma automação, e não a de programar: o que ela faz → quando ela roda →
        testar → publicar → o que aconteceu. Desde a spec 22 são as `Abas` do kit, e o painel delas é
        o que rola.
      */}
      <Abas
        rotulo="Seções da automação"
        abas={ABAS_DA_AUTOMACAO.map((a) => ({ id: a.id, rotulo: a.rotulo }))}
        ativa={aba}
        aoTrocar={(id) => setAba(id as AbaDaAutomacao)}
        classe="v2-elastico hugi-automacoes-abas"
      >
        {() => (
          <>
      {aba === "passos" && (
        <>
    {/*
      --- Os degraus (§4), como SEGMENTADO e não como uma segunda fileira de abas ---------------

      ⚠️ Com as abas em cima, os três degraus na `Aba` pintavam a ativa de `--acao-forte` cheio e
      ficavam logo abaixo do `role="tablist"` — duas fileiras de aba empilhadas, a de baixo com um
      botão azul cheio que lia como AÇÃO. Eles não são irmãos das abas: são três VISTAS dos mesmos
      passos, e um segmentado rotulado diz isso. Achado abrindo a tela em 05/09.
    */}
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-[color:var(--tinta-fraca)]">Ver como</span>
      <div className="inline-flex rounded-[10px] border border-[color:var(--borda)] p-0.5">
        <Degrauzinho
          ativa={degrau === "simples"}
          desabilitada={dados.simples === null && !simples}
          motivo={dados.porqueNaoCabeNoSimples}
          onClick={() => setDegrau("simples")}
        >
          Simples
        </Degrauzinho>
        <Degrauzinho ativa={degrau === "detalhado"} onClick={() => setDegrau("detalhado")}>
          Detalhado
        </Degrauzinho>
        <Degrauzinho ativa={degrau === "desenho"} onClick={() => setDegrau("desenho")}>
          Desenho
        </Degrauzinho>
      </div>
      {dados.porqueNaoCabeNoSimples && (
        // A regra 2 do §4.5: descer só se couber, e NUNCA converter com perda. Em vez de desabilitar
        // calado, a tela diz por quê.
        <span className="text-xs text-[color:var(--tinta-fraca)]">{dados.porqueNaoCabeNoSimples}</span>
      )}
    </div>

    {/*
      A porta de entrada, e ela só aparece para quem NÃO tem fluxo publicado.
      §4 e §7: começar do zero é onde a automação morre. Quem já tem fluxo não precisa disto na frente —
      mostrar a galeria para quem já montou é oferecer para jogar fora o que ele fez.
    */}
    {!dados.fluxo?.versao && (
      <Comecar
        escolhido={comeco}
        aoEscolher={setComeco}
        filas={filas}
        aoTerminarEntrevista={(f) => {
          mudarSimples({ ...f, opcoes: [...f.opcoes] });
          setPassos(daFormaSimples(f).map((x) => ({ ...x, opcoes: [...x.opcoes] })));
          setDegrau("simples");
          setComeco("escolher");
          setAviso(
            "Pronto. Confira as frases e escolha a fila de cada assunto — o que ficou sem fila aparece na " +
              "lista de problemas.",
          );
        }}
      />
    )}

    {degrau === "simples" && simples ? (
      <Simples valor={simples} aoMudar={mudarSimples} filas={filas} etiquetas={etiquetas} />
    ) : degrau === "desenho" ? (
      <Desenho passos={paraValidar} filas={filas} uso={uso} aoClicarNoPasso={irAoPasso} />
    ) : (
      <Detalhado
        passos={passos}
        aoMudar={mudarPassos}
        filas={filas}
        etiquetas={etiquetas}
        destacado={destacado}
      />
    )}

        </>
      )}

      {aba === "quando" && (
        <>
    {/* --- Quando roda, e a saída ----------------------------------------------------------- */}
    {/* Os dois cartões de configuração lado a lado: gatilho e desistência. Empilhados eram 900px. */}
    <div className="grid items-start gap-4 lg:grid-cols-2 [&>*]:min-w-0">
    <div className="vidro flex flex-col gap-3 p-4">
      <p className="text-sm font-medium">
        {usaBlocos(paraValidar) ? "Quando o fluxo começa" : "Quando o menu aparece"}
      </p>
      {QUANDO.map((o) => {
        // A regra vem do DOMÍNIO (`gatilhoCombinaComAForma`), que é o mesmo que `publicarFluxo` usa.
        // Enquanto era um `soComBlocos` escrito aqui, a tela era a única guarda — e ela não é portão.
        const indisponivel = !gatilhoCombinaComAForma(o.id, paraValidar).serve;
        return (
          <div key={o.id} className="flex flex-col gap-1">
            <label
              className={
                // A opacidade fica no CONTROLE, nunca no texto: a explicação existe para ser lida, e
                // `opacity-60` sobre `--tinta-fraca` a leva para perto de 2,6:1. É a mesma correção
                // que o item pendente da barra lateral recebeu em 02/09.
                "flex items-start gap-2 " + (indisponivel ? "cursor-not-allowed" : "cursor-pointer")
              }
            >
              <input
                type="radio"
                name="quando"
                className={"mt-1" + (indisponivel ? " opacity-60" : "")}
                checked={quando === o.id}
                disabled={indisponivel}
                onChange={() => {
                  setQuando(o.id);
                  // Trocar de gatilho limpa o parâmetro: um id de etiqueta guardado num gatilho de texto é
                  // campo fantasma — invisível na tela, gravado no banco, e lido no dia em que alguém
                  // trocar de volta.
                  setQuandoValor("");
                }}
              />
              <span>
                <span className={"text-sm font-medium" + (indisponivel ? " text-[color:var(--tinta-fraca)]" : "")}>{o.rotulo}</span>
                <span className="block text-xs text-[color:var(--tinta-fraca)]">{o.frase}</span>
                {indisponivel && (
                  <span className="block text-xs text-[color:var(--tinta-fraca)]">
                    Este gatilho é do fluxo com blocos. Este fluxo é um menu — troque o tipo de algum passo
                    em <strong>Detalhado</strong> e ele fica disponível.
                  </span>
                )}
              </span>
            </label>

            {quando === o.id && o.valor === "texto" && (
              <label className="ml-6 flex flex-col gap-1">
                <span className="text-xs font-medium">A palavra ou expressão</span>
                <input
                  className="campo max-w-sm"
                  value={quandoValor}
                  onChange={(e) => setQuandoValor(e.target.value)}
                  placeholder="orçamento"
                />
                <span className="text-xs text-[color:var(--tinta-fraca)]">
                  Compara sem acento e sem diferenciar maiúscula: “orçamento” e “orcamento” casam os dois.
                  Sem palavra, o fluxo dispararia em toda mensagem.
                </span>
              </label>
            )}

            {quando === o.id && o.valor === "etiqueta" && (
              <label className="ml-6 flex flex-col gap-1">
                <span className="text-xs font-medium">Qual etiqueta</span>
                <select
                  className="campo max-w-sm"
                  value={quandoValor}
                  onChange={(e) => setQuandoValor(e.target.value)}
                >
                  <option value="">escolha uma etiqueta</option>
                  {etiquetas.map((et) => (
                    <option key={et.id} value={et.id}>
                      {et.nome}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {quando === o.id && o.valor === "motivo" && (
              <div className="ml-6">
                <MotivoDeEncerramento valor={quandoValor || null} aoEscolher={(v) => setQuandoValor(v ?? "")} />
              </div>
            )}

            {quando === o.id && o.valor === "etapa" && (
              <div className="ml-6">
                <EtapaDoFunilParaOGatilho valor={quandoValor || null} aoEscolher={(v) => setQuandoValor(v ?? "")} />
              </div>
            )}
          </div>
        );
      })}
      <p className="text-xs text-[color:var(--tinta-fraca)]">
        Isto vale a partir da próxima publicação — mudar aqui não muda o que está no ar.
      </p>
    </div>

    <div className="vidro flex flex-col gap-3 p-4">
      <div>
        <p className="text-sm font-medium">
          {usaBlocos(paraValidar) ? "Quando o fluxo desiste" : "Se o contato não escolher nada"}
        </p>
        <p className="mt-0.5 text-xs text-[color:var(--tinta-fraca)]">
          {usaBlocos(paraValidar)
            ? "Para onde vai a conversa quando um passo estoura um teto, ou quando um bloco manda para gente."
            : "Um menu que insiste para sempre é um cliente conversando com uma parede."}
        </p>
      </div>
      {/*
        "Repetir no máximo" é `passos_sem_resposta`, e ele é do MENU: o motor de blocos não repete pergunta
        nenhuma — quem insiste lá é o bloco de reengajamento, com regra própria. Mostrar o campo num fluxo de
        blocos é oferecer um ajuste que não faz efeito, que é o defeito que este projeto persegue.
      */}
      <label className={"flex items-center gap-2 " + (usaBlocos(paraValidar) ? "hidden" : "")}>
        <span className="text-xs font-medium">Repetir no máximo</span>
        <input
          type="number"
          className="campo w-20"
          min={1}
          max={5}
          value={repeticoes}
          onChange={(e) => setRepeticoes(Number(e.target.value))}
        />
        <span className="text-xs text-[color:var(--tinta-fraca)]">vezes</span>
      </label>
      {/* Rótulo ao lado do campo, os dois `min-w-0`: a mesma razão do "Identificador" no editor do
          passo — sem eles o par pede mais que os 203px da coluna e o select vaza 23px do cartão. */}
      <label className="flex min-w-0 items-center gap-2">
        <span className="text-xs font-medium">Mandar para</span>
        <select
          className="campo min-w-0"
          value={filaDesistencia}
          onChange={(e) => setFilaDesistencia(e.target.value)}
        >
          <option value="">a fila padrão do canal</option>
          {filas.map((f) => (
            <option key={f.id} value={f.id}>
              {f.nome}
            </option>
          ))}
        </select>
      </label>
    </div>
    </div>

    {/*
      --- Disparo proativo (§3.2) -----------------------------------------------------------

      As três guardas ditas ANTES de salvar, que é o pedido literal da spec: *"a tela precisa dizer isso no
      momento de configurar, não na hora do erro"*. Os números vêm do servidor — contar contato com opt-in na
      tela exigiria mandar a base inteira para o navegador.

      Só aparece em fluxo de blocos: um menu de triagem responde quem escreve, ele não começa conversa.
    */}
    {usaBlocos(paraValidar) && (
      <div className="vidro flex flex-col gap-3 p-4">
        <div>
          <p className="text-sm font-medium">Disparo proativo</p>
          <p className="mt-0.5 text-xs text-[color:var(--tinta-fraca)]">
            Começar a conversa você, em vez de esperar o contato. Tem três regras, e nenhuma é nossa
            escolha — são da plataforma e da lei.
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium">1. Opt-in registrado</p>
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            <strong className="tabular-nums">{proativo.contatosComOptin}</strong> contato(s) podem ser
            alcançados;{" "}
            <strong className="tabular-nums">{proativo.contatosSemOptin}</strong> não, porque não há
            consentimento registrado. Quem escreve para você ganha opt-in automaticamente — importado de
            planilha, não.
          </p>
          {proativo.contatosSemOptin > 0 && (
            <p className="text-xs" style={{ color: "var(--atencao-texto)" }}>
              Mandar para quem não pediu é o caminho mais rápido para o seu número ser bloqueado pela Meta.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium">2. Janela e template</p>
          {proativo.podeAbordar === null ? (
            <p className="text-xs text-[color:var(--tinta-fraca)]">
              Publique o fluxo para saber se ele consegue abordar.
            </p>
          ) : proativo.podeAbordar ? (
            <p className="text-xs text-[color:var(--tinta-fraca)]">
              Este fluxo consegue começar conversa neste canal.
            </p>
          ) : (
            <p className="text-xs" style={{ color: "var(--atencao-texto)" }}>
              {proativo.porQueNaoAborda}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium">3. Categoria, porque a diferença é a fatura</p>
          {proativo.categoriasDeTemplate.length === 0 ? (
            <p className="text-xs text-[color:var(--tinta-fraca)]">
              Não há template aprovado sincronizado. Fora da janela de 24 horas, alcançar o contato abre
              conversa nova e é cobrado pela Meta — sincronize em Canais para ver em qual categoria.
            </p>
          ) : (
            <p className="text-xs text-[color:var(--tinta-fraca)]">
              Seus templates aprovados:{" "}
              {proativo.categoriasDeTemplate.map((c) => `${c.categoria} (${c.quantos})`).join(", ")}.{" "}
              <strong>MARKETING é sempre cobrado</strong>, inclusive quando o contato não responde.
            </p>
          )}
        </div>

        <p className="text-xs text-[color:var(--tinta-fraca)]">
          O disparo é agendado em <strong>Agenda e lembretes</strong> — ele usa o mesmo relógio dos
          lembretes, e não um segundo. As três regras são conferidas de novo no horário: contato que pedir
          para parar no meio não recebe.
        </p>
      </div>
    )}

        </>
      )}

      {aba === "testar" && <Teste passos={paraValidar} repeticoes={repeticoes} />}

      {aba === "publicar" && (
        <>
    {/* --- Salvar × publicar, e o diff no meio (§3.3) --------------------------------------- */}
    {/*
      O QUE ESTÁ NO AR, em três respostas (spec 23): quando roda, em qual número e desde quando. A ação é o primário
      do cabeçalho (Ligar / Publicar alterações) — esta aba não tem um segundo botão de publicar.
    */}
    <dl className="v2-cartao hugi-fluxo-no-ar" aria-label="No ar">
      <div>
        <dt>Quando roda</dt>
        <dd>
          {noAr
            ? `${ROTULO_DO_GATILHO[dados.fluxo?.quando ?? "nunca"] ?? ""}${dados.fluxo?.quando === "mensagem_contendo" && dados.fluxo.quandoValor ? ` "${dados.fluxo.quandoValor}"` : ""}`
            : nuncaPublicada
              ? "Ainda não está no ar"
              : "Desligada"}
        </dd>
      </div>
      <div>
        <dt>Em qual número</dt>
        <dd>
          {(conexoes.data?.conexoes ?? []).filter((c) => c.estado !== "revogada" && c.estado !== "desconectada").length === 0
            ? "Nenhum número conectado"
            : `Em todos: ${(conexoes.data?.conexoes ?? [])
                .filter((c) => c.estado !== "revogada" && c.estado !== "desconectada")
                .map((c) => c.apelido?.trim() || (c.numeroExibicao ? identificadorLegivel(c.numeroExibicao) : c.canal))
                .join(", ")}`}
        </dd>
      </div>
      <div>
        <dt>Desde</dt>
        <dd>{noAr && dados.fluxo?.publicadoEm ? new Date(dados.fluxo.publicadoEm).toLocaleString("pt-BR") : "—"}</dd>
      </div>
    </dl>
    <div className="vidro flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {temRascunho && (
          <button
            type="button"
            className="text-xs underline text-[color:var(--tinta-fraca)]"
            onClick={() => void descartarRascunho(cliente, props.fluxoId)}
          >
            descartar rascunho
          </button>
        )}
      </div>

      {dados.diferenca && !dados.diferenca.semMudanca && (
        <div>
          <p className="text-xs font-medium">O que vai mudar quando você publicar</p>
          <ul className="mt-1 flex flex-col gap-0.5 text-xs text-[color:var(--tinta-fraca)]">
            {dados.diferenca.adicionados.map((a) => (
              <li key={`a-${a}`}>
                <strong>+</strong> passo “{a}”
              </li>
            ))}
            {dados.diferenca.removidos.map((r) => (
              <li key={`r-${r}`}>
                <strong>−</strong> passo “{r}”
              </li>
            ))}
            {dados.diferenca.alterados.map((m) => (
              <li key={`m-${m.passo}`}>
                <strong>~</strong> “{m.passo}”: {m.oQue.join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      {temRascunho && (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">Por que está mudando (opcional)</span>
          <input
            className="campo"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Ex.: separei o financeiro da recepção"
          />
          <span className="text-xs text-[color:var(--tinta-fraca)]">
            Fica no histórico. Vale ouro no dia em que alguém perguntar por que mudou.
          </span>
        </label>
      )}
    </div>

    {versoes.length > 0 && (
      <div className="vidro flex flex-col gap-2 p-4">
        <p className="text-sm font-medium">Publicações</p>
        <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
          {versoes.map((v) => (
            <li key={v.versao} className="flex flex-wrap items-baseline gap-2 py-1.5 text-sm">
              <span className="w-8 shrink-0 tabular-nums">v{v.versao}</span>
              <span className="min-w-0 flex-1 text-[color:var(--tinta-fraca)]">
                {v.nota ?? "sem nota"}
              </span>
              <span className="shrink-0 text-xs text-[color:var(--tinta-fraca)]">
                {new Date(v.publicadoEm).toLocaleString("pt-BR")}
              </span>
            </li>
          ))}
        </ul>
      </div>
    )}

        </>
      )}

      {aba === "atividade" && (
        <>
    {/* --- O que ele fez ------------------------------------------------------------------- */}
    <div className="vidro flex flex-col gap-2 p-4">
      {/*
        O número vem da tabela do motor CERTO — `fluxo_estado` para o menu, `execucoes_de_fluxo` para blocos.
        A tela dizia "passaram pelo menu" sempre, e num fluxo de blocos isso exibia execuções antigas de
        menu embaixo de um fluxo que não é menu: dado real, da coisa errada. Ninguém duvida de um número
        que está ali.
      */}
      <p className="text-sm font-medium">
        {uso.deBlocos ? "Conversas que passaram por este fluxo" : "Conversas que passaram pelo menu"}
      </p>
      <div className="flex gap-6">
        <div>
          <p className="text-2xl font-medium tabular-nums">{uso.emAndamento}</p>
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            {uso.deBlocos ? "no fluxo agora" : "no menu agora"}
          </p>
        </div>
        <div>
          <p className="text-2xl font-medium tabular-nums">{uso.terminados}</p>
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            {uso.deBlocos ? "chegaram ao fim" : "já encaminhadas"}
          </p>
        </div>
        {uso.deBlocos && (
          <div>
            <p
              className="text-2xl font-medium tabular-nums"
              style={(uso.estourados ?? 0) > 0 ? { color: "var(--atencao-texto)" } : undefined}
            >
              {uso.estourados ?? 0}
            </p>
            <p className="text-xs text-[color:var(--tinta-fraca)]">estouraram um teto</p>
          </div>
        )}
      </div>
      {uso.ultimos.length === 0 ? (
        <p className="text-sm text-[color:var(--tinta-fraca)]">
          {uso.deBlocos
            ? "Nada ainda. Cada conversa que entrar no fluxo aparece aqui, com onde ela parou."
            : "Nada ainda. Cada conversa que entrar no menu aparece aqui, com o motivo de ter saído."}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-[color:var(--borda)]">
          {uso.ultimos.map((u) => (
            <li key={u.atendimentoId} className="flex flex-wrap items-baseline gap-2 py-1.5 text-sm">
              <span className="font-mono text-xs">{u.protocolo ?? "—"}</span>
              <span className="text-[color:var(--tinta-fraca)]">
                {u.terminado ? (u.terminouPorque ?? "encaminhada") : `no passo “${u.passoAtual}”`}
              </span>
              <span className="ml-auto shrink-0 text-xs text-[color:var(--tinta-fraca)]">
                {new Date(u.em).toLocaleString("pt-BR")}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
        </>
      )}
          </>
        )}
      </Abas>
    </PaginaInteira>
  );
}

/**
 * O degrau 1 (§4.1): quatro campos, e **nenhum conceito de nó**.
 *
 * Quem preenche é a pessoa que atende, e o que ela vê são frases e destinos. "Passo", "chave" e "vaiPara"
 * não aparecem em lugar nenhum desta função — é isso que faz a fricção sumir.
 */
function Simples(props: {
  readonly valor: FormaSimples;
  readonly aoMudar: (f: FormaSimples) => void;
  readonly filas: readonly { id: string; nome: string }[];
  readonly etiquetas: readonly { id: string; nome: string }[];
}) {
  const { valor, aoMudar } = props;
  const mudarOpcao = (i: number, mudanca: Partial<FormaSimples["opcoes"][number]>) =>
    aoMudar({ ...valor, opcoes: valor.opcoes.map((o, j) => (j === i ? { ...o, ...mudanca } : o)) });

  return (
    <div className="vidro flex flex-col gap-4 p-4">
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium">Como você cumprimenta</span>
        <input
          className="campo"
          value={valor.cumprimento}
          onChange={(e) => aoMudar({ ...valor, cumprimento: e.target.value })}
          placeholder="Oi! Aqui é a Clínica Exemplo."
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium">O que você pergunta</span>
        <input
          className="campo"
          value={valor.pergunta}
          onChange={(e) => aoMudar({ ...valor, pergunta: e.target.value })}
          placeholder="Como podemos ajudar?"
        />
      </label>

      <div className="flex flex-col gap-2">
        <div>
          <p className="text-xs font-medium">As opções, e para onde cada uma vai</p>
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            O contato responde o número. A etiqueta marca quem chegou por aquela opção.
          </p>
        </div>
        {valor.opcoes.map((o, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input
              className="campo w-14"
              value={o.quando}
              onChange={(e) => mudarOpcao(i, { quando: e.target.value })}
              placeholder="1"
            />
            <input
              className="campo min-w-40 flex-1"
              value={o.rotulo}
              onChange={(e) => mudarOpcao(i, { rotulo: e.target.value })}
              placeholder="Agendamento"
            />
            <span className="text-xs text-[color:var(--tinta-fraca)]">vai para</span>
            <select
              className="campo w-36"
              value={o.filaId ?? ""}
              onChange={(e) => mudarOpcao(i, { filaId: e.target.value || null })}
            >
              <option value="">fila padrão</option>
              {props.filas.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}
                </option>
              ))}
            </select>
            <select
              className="campo w-36"
              value={o.etiquetaId ?? ""}
              onChange={(e) => mudarOpcao(i, { etiquetaId: e.target.value || null })}
            >
              <option value="">sem etiqueta</option>
              {props.etiquetas.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nome}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="text-xs underline text-[color:var(--tinta-fraca)]"
              onClick={() => aoMudar({ ...valor, opcoes: valor.opcoes.filter((_, j) => j !== i) })}
            >
              remover
            </button>
          </div>
        ))}
        <button
          type="button"
          className="self-start text-xs underline"
          onClick={() =>
            aoMudar({
              ...valor,
              opcoes: [
                ...valor.opcoes,
                { quando: String(valor.opcoes.length + 1), rotulo: "", filaId: null, etiquetaId: null },
              ],
            })
          }
        >
          + opção
        </button>
      </div>

      {/*
        `Integração` como destino é o §4.4, e ela NÃO aparece aqui ainda — de propósito.
        Oferecer o destino sem haver integração configurada seria um seletor que não pode ser usado; dizer
        que falta é mais honesto que mostrar vazio.
      */}
      <p className="text-xs text-[color:var(--tinta-fraca)]">
        Hoje toda opção termina numa fila.
      </p>
    </div>
  );
}

/** O degrau 2: a lista de passos. É o editor que já existia, agora com destaque e âncora por passo. */
/**
 * Os nove tipos de passo, com a frase que explica cada um.
 *
 * A ordem não é alfabética: é a frequência de uso. `Menu` e `Mensagem` primeiro porque são 90% dos fluxos;
 * `Sistema externo` perto do fim porque é o que exige mais cuidado, e ler os outros antes ajuda a decidir se
 * ele é necessário.
 */
const TIPOS: readonly { id: TipoDePasso; rotulo: string; frase: string }[] = [
  { id: "menu", rotulo: "Menu", frase: "Pergunta e oferece opções numeradas." },
  { id: "mensagem", rotulo: "Mensagem", frase: "Só manda um texto e segue." },
  { id: "perguntar", rotulo: "Pergunta aberta", frase: "Pergunta e guarda o que o contato escrever." },
  { id: "condicional", rotulo: "Condição", frase: "Bifurca conforme o que foi coletado." },
  { id: "esperar", rotulo: "Esperar", frase: "Pausa o fluxo por um tempo." },
  /*
    ⚠️ ESTA FRASE É UMA ENUMERAÇÃO, e enumeração envelhece calada: ela é o único lugar em que quem
    escolhe o tipo de passo lê o que "Ação" abrange. Ficou faltando `dar_como_perdida` uma vez, e
    `abrir_no_funil` de novo em 06/09 — achado ABRINDO a tela, porque nenhum teste compara esta lista
    com os tipos de `AcaoInterna`. **Quem acrescentar uma sexta ação atualiza esta linha junto.**
  */
  { id: "acao", rotulo: "Ação", frase: "Etiqueta, muda de fila, abre um card no funil, finaliza, ou dá a oportunidade como perdida." },
  { id: "agente", rotulo: "Assistente", frase: "Delega este passo ao assistente, com objetivo e saídas." },
  { id: "ferramenta", rotulo: "Sistema externo", frase: "Consulta um sistema seu por HTTP." },
  {
    id: "reengajar",
    rotulo: "Reengajar",
    frase: "Insiste com quem sumiu, e para na hora em que a pessoa responde.",
  },
  { id: "fim", rotulo: "Fim", frase: "Encerra o fluxo aqui." },
];

const OPERADORES: readonly { id: string; rotulo: string }[] = [
  { id: "igual", rotulo: "é igual a" },
  { id: "contem", rotulo: "contém" },
  { id: "existe", rotulo: "tem algum valor" },
  { id: "nao_existe", rotulo: "está vazia" },
];

function Detalhado(props: {
  readonly passos: PassoCompleto[];
  readonly aoMudar: (p: PassoCompleto[]) => void;
  readonly filas: readonly { id: string; nome: string }[];
  readonly etiquetas: readonly { id: string; nome: string }[];
  readonly destacado: string | null;
}) {
  const { passos, aoMudar } = props;

  const mudarPasso = (i: number, mudanca: Partial<PassoCompleto>) =>
    aoMudar(passos.map((p, j) => (j === i ? { ...p, ...mudanca } : p)));

  /**
   * Trocar o tipo LIMPA o que não pertence ao tipo novo.
   *
   * Sem isto, um passo que foi condicional e virou mensagem continuaria carregando `se`, `entao` e `senao` no
   * `jsonb` — invisível na tela, gravado no banco, e lido pelo motor no dia em que alguém trocar de volta. O
   * campo fantasma é o defeito mais difícil de diagnosticar num editor, porque a tela está certa.
   *
   * `proximo` e `pergunta` sobrevivem: os dois são usados por quase todos os tipos, e apagá-los faria a pessoa
   * reescrever o texto ao experimentar um tipo diferente.
   */
  const trocarTipo = (i: number, tipo: TipoDePasso) => {
    const p = passos[i];
    if (!p) return;
    const limpo: PassoCompleto = {
      chave: p.chave,
      pergunta: p.pergunta,
      // Menu guarda as opções; qualquer outro tipo não as usa, e mantê-las faria a lista voltar ao trocar.
      opcoes: tipo === "menu" ? p.opcoes : [],
      tipo,
      proximo: p.proximo ?? null,
      ...(tipo === "perguntar" ? { guardaEm: p.guardaEm ?? null } : {}),
      ...(tipo === "condicional"
        ? { se: p.se ?? [{ variavel: "", operador: "igual", valor: "" }], entao: p.entao ?? null, senao: p.senao ?? null }
        : {}),
      ...(tipo === "esperar" ? { esperarMin: p.esperarMin ?? 60 } : {}),
      ...(tipo === "acao" ? { acao: p.acao ?? { tipo: "etiquetar", etiquetaId: null } } : {}),
      ...(tipo === "agente"
        ? {
            agente:
              p.agente ?? { objetivo: "", saidasEsperadas: ["resolvido", "precisa_de_gente"], maxTurnos: 3, aoEstourar: null },
          }
        : {}),
      ...(tipo === "reengajar"
        ? {
            reengajar:
              p.reengajar ?? {
                tentativas: 3,
                intervaloMin: 60 * 24,
                mensagens: ["Passando para saber se ainda posso ajudar."],
                aoDesistir: null,
                aoResponder: null,
              },
          }
        : {}),
      ...(tipo === "ferramenta"
        ? {
            ferramenta:
              p.ferramenta ?? {
                nome: "",
                url: "",
                metodo: "GET" as const,
                timeoutSeg: 5,
                guardaEm: null,
                aoFalhar: null,
                aoVirVazio: null,
              },
          }
        : {}),
    };
    aoMudar(passos.map((outro, j) => (j === i ? limpo : outro)));
  };

  const mudarOpcao = (i: number, j: number, mudanca: Partial<PassoDoFluxo["opcoes"][number]>) =>
    aoMudar(
      passos.map((p, pi) =>
        pi === i ? { ...p, opcoes: p.opcoes.map((o, oj) => (oj === j ? { ...o, ...mudanca } : o)) } : p,
      ),
    );

  // O passo que está sendo editado. Começa no destacado (quem clicou num problema chega no passo
  // certo), senão no primeiro. Chave que sumiu (passo removido) volta para o primeiro.
  // ...ou no passo da URL (`#passo-<chave>`, o mesmo `id` que cada linha da lista carrega): é o que faz
  // "olha esse passo" ser um link que se manda a um colega, como a conversa aberta é no inbox.
  const daUrl = typeof window !== "undefined" ? /^#passo-(.+)$/.exec(window.location.hash)?.[1] ?? null : null;
  const [escolhido, setEscolhido] = useState<string | null>(props.destacado ?? daUrl);
  useEffect(() => {
    if (props.destacado) setEscolhido(props.destacado);
  }, [props.destacado]);
  const indice = Math.max(0, passos.findIndex((x) => x.chave === escolhido));
  const p = passos[indice];
  const i = indice;

  const adicionar = () =>
    aoMudar([
      ...passos,
      {
        chave: `passo${passos.length + 1}`,
        pergunta: "",
        opcoes: [{ quando: "1", vaiPara: null, filaId: null, etiquetaId: null }],
      },
    ]);

  return (
    /*
      LISTA À ESQUERDA, EDITOR À DIREITA — o desenho da prévia (`docs/previas/painel-denso.html`).
      Até 03/09 cada passo era um cartão de formulário numa grade de duas colunas: cinco passos eram
      7.687px de rolagem, e o owner chamou isso de "rolagem quilométrica". Agora a lista mostra o
      que cada passo É (ícone do tipo, identificador, a primeira linha, para onde vai) e só o passo
      escolhido abre o formulário.
    */
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(300px,2fr)_minmax(360px,3fr)] [&>*]:min-w-0">
      <div className="vidro flex flex-col gap-1.5 p-3">
        <p className="px-1 text-sm font-medium">Os passos</p>
        {/* A lista rola dentro do cartão; a página não. Quem tem 14 passos vê o editor parado ao lado. */}
        <ol className="flex max-h-[calc(100vh-340px)] min-h-40 flex-col gap-1.5 overflow-y-auto pr-1">
          {passos.map((passo, k) => {
            const tipo = TIPOS.find((t) => t.id === (passo.tipo ?? "menu"));
            const ativo = k === indice;
            return (
              <li key={k} id={`passo-${passo.chave}`}>
                <button
                  type="button"
                  onClick={() => setEscolhido(passo.chave)}
                  aria-current={ativo ? "true" : undefined}
                  className={[
                    /*
                      `flex-wrap` mais o `min-w-24` do miolo, logo abaixo — os dois juntos, e nenhum
                      sozinho resolve.

                      O destino é `shrink-0`, então quem encolhe é sempre o miolo; com `min-w-0` ele
                      encolhia até **19px** num item de 207 (medido a 375px, no passo com destino
                      "→ pausa / etiquetar"). A lista deixava de dizer QUAL passo é cada linha, que é
                      a única coisa que ela existe para dizer.

                      O piso sozinho não bastaria: sem `flex-wrap` ele viraria transbordo. E o wrap
                      sozinho não bastaria: com base zero o miolo "cabe" em qualquer sobra e a linha
                      nunca quebra. Com os dois, a 375px o destino desce para a linha de baixo (item
                      de 83px em vez de 54) e o nome fica inteiro. **A 1440px nada muda** — medido:
                      os três continuam na mesma linha, altura 54.
                    */
                    "flex w-full flex-wrap items-center gap-3 rounded-xl border px-3 py-2 text-left transition",
                    ativo
                      ? "border-[color:var(--acao)] bg-[color:var(--acao-clara)] shadow-[0_0_0_1px_var(--acao)]"
                      : "border-transparent hover:bg-[color:var(--vidro-controle)]",
                    props.destacado === passo.chave && !ativo ? "border-[color:var(--atencao)]" : "",
                  ].join(" ")}
                >
                  <Ladrilho tom={TOM_DO_TIPO[passo.tipo ?? "menu"]}>{iconeDoTipo(passo.tipo ?? "menu")}</Ladrilho>
                  <span className="min-w-24 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      {/* "Passo 1", e não a chave interna (spec 23): o identificador só aparece no campo dele. */}
                      <span className="text-xs font-semibold text-[color:var(--tinta-forte)]">Passo {k + 1}</span>
                      <span className="rounded-full border border-[color:var(--borda)] px-1.5 text-[10.5px] text-[color:var(--tinta-fraca)]">{tipo?.rotulo ?? passo.tipo}</span>
                    </span>
                    <span className="block truncate text-xs text-[color:var(--tinta-fraca)]">{resumoDoPasso(passo)}</span>
                  </span>
                  <span className="shrink-0 text-[11px] text-[color:var(--tinta-fraca)]">{destinoDoPasso(passo, passos)}</span>
                </button>
              </li>
            );
          })}
        </ol>
        <button type="button" className="mt-1 self-start rounded-lg px-3 py-1.5 text-xs font-medium text-[color:var(--acao-texto)] hover:bg-[color:var(--acao-clara)]" onClick={adicionar}>
          + adicionar passo
        </button>
      </div>

      {p ? (
        <div key={i} className="vidro flex flex-col gap-3 p-4">
          <div className="flex items-center gap-3">
            <Ladrilho tom={TOM_DO_TIPO[p.tipo ?? "menu"]}>{iconeDoTipo(p.tipo ?? "menu")}</Ladrilho>
            <p className="min-w-0 flex-1 text-sm font-medium">
              Passo {i + 1}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/*
              Os DOIS `min-w-0` são o que faz esta linha caber a 375px, e o motivo é o mesmo nos dois
              níveis: `min-width` de um item de flex é `auto`, e `auto` resolve para o tamanho
              intrínseco do conteúdo. Sem eles, o rótulo (74px) mais o campo (`w-40`, 160px) pedem
              242px num espaço de 203, o `flex-wrap` não tem onde quebrar porque o par é um item só,
              e o campo **vaza 39px para fora do cartão**. Ele fica visível — o cartão não corta —,
              então o sintoma é o editor com um campo pendurado fora da borda.
            */}
            <label className="flex min-w-0 items-center gap-2">
              <span className="text-xs font-medium">Identificador</span>
              <input
                className="campo w-40 min-w-0 font-mono text-xs"
                value={p.chave}
                onChange={(e) => mudarPasso(i, { chave: e.target.value })}
              />
            </label>
            {i > 0 && (
              <Botao tipo="secundario" onClick={() => aoMudar(passos.filter((_, j) => j !== i))}>
                Remover passo
              </Botao>
            )}
          </div>

          {/*
            O tipo do passo (§5.1 da spec 12).

            `menu` é o padrão porque é o que existia antes de haver tipo — e trocar o padrão mudaria o
            comportamento de todo fluxo já salvo. Ver `usaBlocos` no módulo puro.
          */}
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">O que este passo faz</span>
            <select
              className="campo"
              value={p.tipo ?? "menu"}
              onChange={(e) => trocarTipo(i, e.target.value as TipoDePasso)}
            >
              {TIPOS.map((tt) => (
                <option key={tt.id} value={tt.id}>
                  {tt.rotulo} — {tt.frase}
                </option>
              ))}
            </select>
          </label>

          {(p.tipo ?? "menu") !== "menu" ? (
            <CamposDoBloco
              passo={p}
              passos={passos}
              filas={props.filas}
              etiquetas={props.etiquetas}
              aoMudar={(mudanca) => mudarPasso(i, mudanca)}
            />
          ) : (
          <>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">
              {i === 0 ? "A pergunta que abre a conversa" : "A pergunta deste passo"}
            </span>
            <textarea
              className="campo min-h-24"
              value={p.pergunta}
              onChange={(e) => mudarPasso(i, { pergunta: e.target.value })}
              placeholder={"Como podemos ajudar?\n1 - Marcar consulta\n2 - Financeiro"}
            />
          </label>

          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium">O que fazer com cada resposta</p>
            {p.opcoes.map((o, j) => (
              <div key={j} className="flex flex-wrap items-center gap-2">
                <input
                  className="campo w-20"
                  value={o.quando}
                  onChange={(e) => mudarOpcao(i, j, { quando: e.target.value })}
                  placeholder="1"
                />
                <select
                  className="campo w-44"
                  value={o.vaiPara ?? ""}
                  onChange={(e) => mudarOpcao(i, j, { vaiPara: e.target.value || null })}
                >
                  <option value="">encaminha para a fila</option>
                  {passos
                    .filter((outro, k) => k !== i && outro.chave.trim() !== "")
                    .map((outro) => (
                      <option key={outro.chave} value={outro.chave}>
                        vai para “{outro.chave}”
                      </option>
                    ))}
                </select>
                {!o.vaiPara && (
                  <>
                    <select
                      className="campo w-36"
                      value={o.filaId ?? ""}
                      onChange={(e) => mudarOpcao(i, j, { filaId: e.target.value || null })}
                    >
                      <option value="">fila padrão</option>
                      {props.filas.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.nome}
                        </option>
                      ))}
                    </select>
                    <select
                      className="campo w-36"
                      value={o.etiquetaId ?? ""}
                      onChange={(e) => mudarOpcao(i, j, { etiquetaId: e.target.value || null })}
                    >
                      <option value="">sem etiqueta</option>
                      {props.etiquetas.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.nome}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <button
                  type="button"
                  className="text-xs underline text-[color:var(--tinta-fraca)]"
                  onClick={() =>
                    aoMudar(
                      passos.map((pp, pi) =>
                        pi === i ? { ...pp, opcoes: pp.opcoes.filter((_, oj) => oj !== j) } : pp,
                      ),
                    )
                  }
                >
                  remover
                </button>
              </div>
            ))}
            <button
              type="button"
              className="self-start text-xs underline"
              onClick={() =>
                aoMudar(
                  passos.map((pp, pi) =>
                    pi === i
                      ? {
                          ...pp,
                          opcoes: [
                            ...pp.opcoes,
                            { quando: "", vaiPara: null, filaId: null, etiquetaId: null },
                          ],
                        }
                      : pp,
                  ),
                )
              }
            >
              + opção
            </button>
          </div>
          </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** A primeira linha do que o passo diz ou faz. */
function resumoDoPasso(p: PassoCompleto): string {
  const tipo = p.tipo ?? "menu";
  if (tipo === "esperar") return `Espera ${p.esperarMin ?? 60} min`;
  if (tipo === "condicional") return (p.se ?? []).map((c) => `${c.variavel || "?"} ${c.operador} ${c.valor}`.trim()).join(" e ") || "Sem condição";
  /*
    ⚠️ O RESUMO É POR TIPO, com uma linha para cada — e não um ternário com fundo em "Etiqueta o
    contato". Ele era assim, e a ação nova nascia rotulada como se etiquetasse: o cartão da lista
    mentia sobre o que o passo faz, que é o defeito que encerra a investigação de quem foi ali
    descobrir por que o card não fechou. Achado ABRINDO a tela, e não por teste.

    Tipo desconhecido devolve o próprio id em vez de escolher um: dizer "não conheço esta ação" é
    honesto; escolher a primeira é o mesmo defeito com outra roupa.
  */
  if (tipo === "acao") {
    const a = p.acao?.tipo;
    if (a === "mover_fila") return "Muda de fila";
    if (a === "finalizar") return "Finaliza";
    if (a === "etiquetar") return "Etiqueta o contato";
    if (a === "dar_como_perdida") return "Dá a oportunidade como perdida";
    if (a === "abrir_no_funil") return "Abre um card no funil";
    return a ? `Ação "${a}"` : "Sem ação escolhida";
  }
  if (tipo === "agente") return p.agente?.objetivo || "Sem objetivo escrito";
  if (tipo === "ferramenta") return p.ferramenta?.nome || p.ferramenta?.url || "Sem sistema configurado";
  if (tipo === "reengajar") return `${p.reengajar?.tentativas ?? 0} tentativa(s)`;
  if (tipo === "fim") return "Encerra o fluxo";
  const linha = (p.pergunta ?? "").split("\n").find((l) => l.trim()) ?? "";
  return linha || (tipo === "menu" ? `${p.opcoes.length} opção(ões)` : "Sem texto");
}

/** Para onde o passo segue — a seta da lista. */
function destinoDoPasso(p: PassoCompleto, passos: readonly PassoCompleto[]): string {
  const tipo = p.tipo ?? "menu";
  if (tipo === "fim") return "";
  if (tipo === "menu") return p.opcoes.length ? `${p.opcoes.length} saída(s)` : "";
  if (tipo === "condicional") return `→ ${p.entao ?? "?"} / ${p.senao ?? "?"}`;
  if (p.proximo) return `→ ${p.proximo}`;
  const k = passos.indexOf(p);
  return k >= 0 && k < passos.length - 1 ? "→ seguinte" : "";
}

const TOM_DO_TIPO: Record<TipoDePasso, TomDoLadrilho> = {
  menu: "ct",
  mensagem: "wa",
  perguntar: "ag",
  condicional: "rl",
  esperar: "cf",
  acao: "fx",
  agente: "ia",
  ferramenta: "in",
  reengajar: "sm",
  fim: "neutro",
};

function iconeDoTipo(tipo: TipoDePasso) {
  const t = 17;
  switch (tipo) {
    case "menu": return <ListOrdered size={t} />;
    case "mensagem": return <MessageSquare size={t} />;
    case "perguntar": return <MessageCircleQuestion size={t} />;
    case "condicional": return <Split size={t} />;
    case "esperar": return <Timer size={t} />;
    case "acao": return <Zap size={t} />;
    case "agente": return <Sparkles size={t} />;
    case "ferramenta": return <Globe size={t} />;
    case "reengajar": return <RefreshCw size={t} />;
    case "fim": return <Square size={t} />;
  }
}

/**
 * Os campos de cada tipo de bloco (§5.1 da spec 12).
 *
 * ## Um seletor de destino, e nunca um campo de texto
 *
 * Todo "para onde vai" é um `<select>` sobre os passos que existem. Digitar o nome do passo é como se criam os
 * destinos órfãos que a validação depois aponta — e o editor que deixa você errar e depois reclama é pior que o
 * que não deixa errar.
 *
 * ## As duas saídas do sistema externo aparecem SEPARADAS, com o motivo escrito
 *
 * §8c: `falha` e `vazio` são situações diferentes. Um par de campos genérico ("saídas de erro") faria quem
 * preenche apontar as duas para o mesmo lugar, que é exatamente o defeito — o contato recebe "ocorreu um
 * problema" quando a resposta certa era "não temos horário essa semana".
 */
function CamposDoBloco(props: {
  readonly passo: PassoCompleto;
  readonly passos: readonly PassoCompleto[];
  readonly filas: readonly { id: string; nome: string }[];
  readonly etiquetas: readonly { id: string; nome: string }[];
  readonly aoMudar: (mudanca: Partial<PassoCompleto>) => void;
}) {
  const { passo: p, aoMudar } = props;
  const tipo = p.tipo ?? "menu";
  // Os agentes da empresa, para o passo `agente` escolher qual assume (0057). Hook incondicional:
  // a regra dos hooks não aceita chamá-lo só quando o tipo é `agente`.
  const agentes = useAgentes();

  const Destino = (d: {
    readonly rotulo: string;
    readonly ajuda?: string;
    readonly valor: string | null | undefined;
    readonly aoEscolher: (v: string | null) => void;
  }) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium">{d.rotulo}</span>
      <select className="campo" value={d.valor ?? ""} onChange={(e) => d.aoEscolher(e.target.value || null)}>
        <option value="">— termina o fluxo aqui —</option>
        {props.passos
          .filter((outro) => outro.chave.trim() !== "" && outro.chave !== p.chave)
          .map((outro) => (
            <option key={outro.chave} value={outro.chave}>
              {outro.chave}
            </option>
          ))}
      </select>
      {d.ajuda && <span className="text-xs text-[color:var(--tinta-fraca)]">{d.ajuda}</span>}
    </label>
  );

  if (tipo === "fim") {
    return (
      <p className="text-xs text-[color:var(--tinta-fraca)]">
        Este passo encerra o fluxo. A conversa fica onde está — na fila, ou com quem já a assumiu.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {(tipo === "mensagem" || tipo === "perguntar" || tipo === "agente") && (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">
            {tipo === "agente" ? "A primeira coisa que o assistente diz (opcional)" : "O texto que o contato recebe"}
          </span>
          <textarea
            className="campo min-h-20"
            value={p.pergunta}
            onChange={(e) => aoMudar({ pergunta: e.target.value })}
            placeholder={tipo === "perguntar" ? "Qual é o seu nome?" : "Recebi seu pedido, já vou verificar."}
          />
        </label>
      )}

      {tipo === "perguntar" && (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">Guardar a resposta em</span>
          <input
            className="campo font-mono text-xs"
            value={p.guardaEm ?? ""}
            onChange={(e) => aoMudar({ guardaEm: e.target.value })}
            placeholder="nome"
          />
          <span className="text-xs text-[color:var(--tinta-fraca)]">
            É por este nome que a condição adiante vai ler o que o contato escreveu. Sem ele, a resposta é
            jogada fora.
          </span>
        </label>
      )}

      {tipo === "condicional" && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium">As condições — todas precisam valer</p>
          {(p.se ?? []).map((c, j) => (
            <div key={j} className="flex flex-wrap items-center gap-2">
              <input
                className="campo w-40 font-mono text-xs"
                value={c.variavel}
                onChange={(e) =>
                  aoMudar({
                    se: (p.se ?? []).map((outro, k) => (k === j ? { ...outro, variavel: e.target.value } : outro)),
                  })
                }
                placeholder="nome, fila, canal, etiqueta"
              />
              <select
                className="campo w-40"
                value={c.operador}
                onChange={(e) =>
                  aoMudar({
                    se: (p.se ?? []).map((outro, k) =>
                      k === j ? { ...outro, operador: e.target.value as typeof outro.operador } : outro,
                    ),
                  })
                }
              >
                {OPERADORES.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.rotulo}
                  </option>
                ))}
              </select>
              {c.operador !== "existe" && c.operador !== "nao_existe" && (
                <input
                  className="campo w-40"
                  value={c.valor ?? ""}
                  onChange={(e) =>
                    aoMudar({
                      se: (p.se ?? []).map((outro, k) => (k === j ? { ...outro, valor: e.target.value } : outro)),
                    })
                  }
                  placeholder="sim"
                />
              )}
              <button
                type="button"
                className="text-xs underline text-[color:var(--tinta-fraca)]"
                onClick={() => aoMudar({ se: (p.se ?? []).filter((_, k) => k !== j) })}
              >
                remover
              </button>
            </div>
          ))}
          <button
            type="button"
            className="self-start text-xs underline"
            onClick={() => aoMudar({ se: [...(p.se ?? []), { variavel: "", operador: "igual", valor: "" }] })}
          >
            + condição
          </button>

          <Destino rotulo="Quando todas valem, vai para" valor={p.entao} aoEscolher={(v) => aoMudar({ entao: v })} />
          <Destino
            rotulo="Quando NÃO valem, vai para"
            ajuda="Obrigatório. Sem esta saída o fluxo engole quem não casa nenhuma condição, e ninguém descobre até alguém reclamar."
            valor={p.senao}
            aoEscolher={(v) => aoMudar({ senao: v })}
          />
        </div>
      )}

      {tipo === "esperar" && (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium">Esperar quantos minutos</span>
          <input
            type="number"
            min={1}
            max={60 * 24 * 7}
            className="campo w-40"
            value={p.esperarMin ?? 60}
            onChange={(e) => aoMudar({ esperarMin: Number(e.target.value) })}
          />
          <span className="text-xs text-[color:var(--tinta-fraca)]">
            O limite é sete dias. Espera longa é um fluxo que ninguém lembra que existe, com o contato dentro.
          </span>
        </label>
      )}

      {tipo === "acao" && (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">O que fazer</span>
            <select
              className="campo"
              value={p.acao?.tipo ?? "etiquetar"}
              onChange={(e) =>
                aoMudar({ acao: { tipo: e.target.value as NonNullable<PassoCompleto["acao"]>["tipo"] } })
              }
            >
              <option value="etiquetar">Etiquetar o contato</option>
              <option value="mover_fila">Mover para outra fila</option>
              <option value="finalizar">Finalizar o atendimento</option>
              <option value="abrir_no_funil">Abrir um card no funil</option>
              <option value="dar_como_perdida">Dar a oportunidade como perdida</option>
            </select>
            <span className="text-xs text-[color:var(--tinta-fraca)]">
              São as mesmas ações que um atendente faz. O fluxo não pode fazer nada que uma pessoa não possa.
            </span>
          </label>

          {p.acao?.tipo === "etiquetar" && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">Qual etiqueta</span>
              <select
                className="campo"
                value={p.acao.etiquetaId ?? ""}
                onChange={(e) => aoMudar({ acao: { ...p.acao!, etiquetaId: e.target.value || null } })}
              >
                <option value="">escolha uma etiqueta</option>
                {props.etiquetas.map((et) => (
                  <option key={et.id} value={et.id}>
                    {et.nome}
                  </option>
                ))}
              </select>
            </label>
          )}

          {p.acao?.tipo === "mover_fila" && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">Para qual fila</span>
              <select
                className="campo"
                value={p.acao.filaId ?? ""}
                onChange={(e) => aoMudar({ acao: { ...p.acao!, filaId: e.target.value || null } })}
              >
                <option value="">escolha uma fila</option>
                {props.filas.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </label>
          )}

          {p.acao?.tipo === "finalizar" && (
            <MotivoDeEncerramento
              valor={p.acao.motivoId ?? null}
              aoEscolher={(v) => aoMudar({ acao: { ...p.acao!, motivoId: v } })}
            />
          )}

          {p.acao?.tipo === "abrir_no_funil" && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">Título do card (opcional)</span>
              <input
                className="campo"
                value={p.acao.tituloDoCard ?? ""}
                maxLength={120}
                placeholder="Orçamento pedido pelo WhatsApp"
                onChange={(e) => aoMudar({ acao: { ...p.acao!, tituloDoCard: e.target.value || null } })}
              />
              <span className="text-xs text-[color:var(--tinta-fraca)]">
                O card nasce no quadro padrão, na primeira etapa. Se o contato já tem um card aberto lá,
                este passo aponta o card que existe para esta conversa em vez de criar outro.
              </span>
            </label>
          )}

          {p.acao?.tipo === "dar_como_perdida" && (
            <MotivoDePerda
              valor={p.acao.motivoDePerdaId ?? null}
              aoEscolher={(v) => aoMudar({ acao: { ...p.acao!, motivoDePerdaId: v } })}
            />
          )}
        </div>
      )}

      {tipo === "agente" && p.agente && (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Qual agente assume este passo</span>
            <select
              className="campo"
              value={p.agente.agenteId ?? ""}
              onChange={(e) => aoMudar({ agente: { ...p.agente!, agenteId: e.target.value || null } })}
            >
              <option value="">Nenhum — só o objetivo abaixo</option>
              {(agentes.data?.agentes ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome}
                  {a.temInstrucoes ? "" : " (sem instruções)"}
                </option>
              ))}
              {p.agente.agenteId && !(agentes.data?.agentes ?? []).some((a) => a.id === p.agente!.agenteId) && (
                <option value={p.agente.agenteId}>Agente arquivado</option>
              )}
            </select>
            <span className="text-xs text-[color:var(--tinta-fraca)]">
              Com um agente escolhido, o passo fala como ele: as instruções, os procedimentos e o material dele
              entram antes do objetivo. É assim que o fluxo de boas-vindas chama a recepção e o de cobrança
              chama o financeiro.
            </span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">O objetivo deste passo</span>
            <textarea
              className="campo min-h-16"
              value={p.agente.objetivo}
              onChange={(e) => aoMudar({ agente: { ...p.agente!, objetivo: e.target.value } })}
              placeholder="Descobrir se o contato quer marcar, remarcar ou cancelar."
            />
            <span className="text-xs text-[color:var(--tinta-fraca)]">
              Sem objetivo o assistente volta a ser o geral, no meio de um caminho que precisa ser previsível.
            </span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">As saídas que ele pode dar, separadas por vírgula</span>
            <input
              className="campo font-mono text-xs"
              value={p.agente.saidasEsperadas.join(", ")}
              onChange={(e) =>
                aoMudar({
                  agente: {
                    ...p.agente!,
                    saidasEsperadas: e.target.value
                      .split(",")
                      .map((x) => x.trim())
                      .filter((x) => x !== ""),
                  },
                })
              }
              placeholder="marcar, remarcar, cancelar"
            />
            <span className="text-xs text-[color:var(--tinta-fraca)]">
              É o contrato: o fluxo só continua se a saída for uma destas. Sem ele não há como ligar o passo
              seguinte.
            </span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Quantos turnos no máximo</span>
            <input
              type="number"
              min={1}
              max={20}
              className="campo w-32"
              value={p.agente.maxTurnos}
              onChange={(e) => aoMudar({ agente: { ...p.agente!, maxTurnos: Number(e.target.value) } })}
            />
          </label>

          <Destino
            rotulo="Quando ele não chega a nenhuma saída, vai para"
            ajuda="Vazio manda a conversa para a fila de desistência. Nunca continua como se tivesse resolvido."
            valor={p.agente.aoEstourar}
            aoEscolher={(v) => aoMudar({ agente: { ...p.agente!, aoEstourar: v } })}
          />
        </div>
      )}

      {tipo === "ferramenta" && p.ferramenta && (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Nome, para você reconhecer no relatório</span>
            <input
              className="campo"
              value={p.ferramenta.nome}
              onChange={(e) => aoMudar({ ferramenta: { ...p.ferramenta!, nome: e.target.value } })}
              placeholder="Listar horários no ERP"
            />
          </label>

          <div className="flex flex-wrap items-end gap-2">
            {/*
              `min-w-40` e não `min-w-0`: o método (`w-24`) e a espera (`w-20`) não encolhem, e com
              base zero o endereço "cabia" no que sobrava — **11px**, um campo de URL do tamanho de
              um caractere. O piso é o que faz o `flex-wrap` acima quebrar de verdade, mandando o
              endereço para a própria linha em vez de espremê-lo. Em tela larga cabem os três e o
              `flex-1` continua dando o resto ao endereço, que é o campo que importa.
            */}
            <label className="flex min-w-40 flex-1 flex-col gap-1">
              <span className="text-xs font-medium">Endereço</span>
              <input
                className="campo font-mono text-xs"
                value={p.ferramenta.url}
                onChange={(e) => aoMudar({ ferramenta: { ...p.ferramenta!, url: e.target.value } })}
                placeholder="https://sistema.suaempresa.com/horarios"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">Método</span>
              <select
                className="campo w-24"
                value={p.ferramenta.metodo}
                onChange={(e) =>
                  aoMudar({ ferramenta: { ...p.ferramenta!, metodo: e.target.value as "GET" | "POST" } })
                }
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">Espera até</span>
              <input
                type="number"
                min={1}
                max={15}
                className="campo w-20"
                value={p.ferramenta.timeoutSeg}
                onChange={(e) =>
                  aoMudar({ ferramenta: { ...p.ferramenta!, timeoutSeg: Number(e.target.value) } })
                }
              />
            </label>
          </div>

          <p className="text-xs text-[color:var(--tinta-fraca)]">
            O Hugi manda as variáveis coletadas, e nada mais — nem o texto da conversa, nem o telefone.
            Endereços de rede interna são recusados: a requisição sairia de dentro da nossa infraestrutura.
          </p>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium">Guardar o resultado em</span>
            <input
              className="campo font-mono text-xs"
              value={p.ferramenta.guardaEm ?? ""}
              onChange={(e) => aoMudar({ ferramenta: { ...p.ferramenta!, guardaEm: e.target.value } })}
              placeholder="horarios"
            />
          </label>

          <Destino
            rotulo="Quando o sistema NÃO responde, vai para"
            ajuda="Obrigatório. Sem esta saída, um sistema fora do ar deixa uma fila de gente parada no meio do fluxo."
            valor={p.ferramenta.aoFalhar}
            aoEscolher={(v) => aoMudar({ ferramenta: { ...p.ferramenta!, aoFalhar: v } })}
          />
          <Destino
            rotulo='Quando ele responde "não há nada", vai para'
            ajuda="Obrigatório, e separado de propósito: vazio não é erro. Tratar como erro faz o contato receber uma mensagem de problema quando a resposta certa era uma informação."
            valor={p.ferramenta.aoVirVazio}
            aoEscolher={(v) => aoMudar({ ferramenta: { ...p.ferramenta!, aoVirVazio: v } })}
          />
        </div>
      )}

      {tipo === "reengajar" && p.reengajar && (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            O contato responder <strong>cancela</strong> as tentativas que faltam. Isso não é uma opção aqui —
            é como o bloco funciona, porque o jeito de errar isto é receber mais três lembretes depois de já
            ter resolvido.
          </p>

          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">Quantas tentativas</span>
              <input
                type="number"
                min={1}
                max={5}
                className="campo w-24"
                value={p.reengajar.tentativas}
                onChange={(e) =>
                  aoMudar({ reengajar: { ...p.reengajar!, tentativas: Number(e.target.value) } })
                }
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium">Intervalo, em minutos</span>
              <input
                type="number"
                min={60}
                className="campo w-32"
                value={p.reengajar.intervaloMin}
                onChange={(e) =>
                  aoMudar({ reengajar: { ...p.reengajar!, intervaloMin: Number(e.target.value) } })
                }
              />
              <span className="text-xs text-[color:var(--tinta-fraca)]">
                Mínimo de 60. Três mensagens em dez minutos é o que faz o contato bloquear o seu número.
              </span>
            </label>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium">O texto de cada tentativa</p>
            {p.reengajar.mensagens.map((m, j) => (
              <div key={j} className="flex items-start gap-2">
                <span className="mt-2 w-4 shrink-0 text-xs text-[color:var(--tinta-fraca)]">{j + 1}</span>
                <textarea
                  className="campo min-h-16 flex-1"
                  value={m}
                  onChange={(e) =>
                    aoMudar({
                      reengajar: {
                        ...p.reengajar!,
                        mensagens: p.reengajar!.mensagens.map((outro, k) => (k === j ? e.target.value : outro)),
                      },
                    })
                  }
                />
                {p.reengajar!.mensagens.length > 1 && (
                  <button
                    type="button"
                    className="mt-2 text-xs underline text-[color:var(--tinta-fraca)]"
                    onClick={() =>
                      aoMudar({
                        reengajar: {
                          ...p.reengajar!,
                          mensagens: p.reengajar!.mensagens.filter((_, k) => k !== j),
                        },
                      })
                    }
                  >
                    remover
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="self-start text-xs underline"
              onClick={() =>
                aoMudar({ reengajar: { ...p.reengajar!, mensagens: [...p.reengajar!.mensagens, ""] } })
              }
            >
              + texto para a próxima tentativa
            </button>
            <span className="text-xs text-[color:var(--tinta-fraca)]">
              Menos textos que tentativas repete o último. Um texto só é o mesmo lembrete repetido.
            </span>
          </div>

          <Destino
            rotulo="Quando as tentativas acabam, vai para"
            ajuda="Obrigatório. Reengajamento sem fim é o produto perseguindo alguém — depois da última tentativa isto vira trabalho de gente."
            valor={p.reengajar.aoDesistir}
            aoEscolher={(v) => aoMudar({ reengajar: { ...p.reengajar!, aoDesistir: v } })}
          />
          <Destino
            rotulo="Quando o contato responde, vai para"
            ajuda="Vazio segue pelo passo seguinte. O cancelamento acontece de qualquer forma."
            valor={p.reengajar.aoResponder ?? null}
            aoEscolher={(v) => aoMudar({ reengajar: { ...p.reengajar!, aoResponder: v } })}
          />
        </div>
      )}

      {tipo !== "condicional" && (
        <Destino rotulo="Depois deste passo, vai para" valor={p.proximo} aoEscolher={(v) => aoMudar({ proximo: v })} />
      )}
    </div>
  );
}

/**
 * O motivo de encerramento, lido do catálogo do tenant.
 *
 * Componente próprio porque ele consulta — e um `useCatalogos` dentro de `CamposDoBloco` rodaria em todo passo,
 * de todo tipo, inclusive nos que não têm ação de finalizar.
 */
/** A etapa do funil que dispara o fluxo (spec 10 §4.4). Ganho e Perdido também são etapas — e entram. */
export function EtapaDoFunilParaOGatilho(props: {
  readonly valor: string | null;
  readonly aoEscolher: (v: string | null) => void;
}) {
  // As etapas de TODOS os quadros (0068), agrupadas: o gatilho pode ser "entrou em Proposta" do quadro
  // de Vendas ou do de Pós-venda — são etapas diferentes, com ids diferentes.
  const q = useEtapasDeTodosOsQuadros();
  const porQuadro = new Map<string, { nome: string; etapas: { id: string; nome: string; tipo: string }[] }>();
  for (const e of q.data?.etapas ?? []) {
    const g = porQuadro.get(e.quadroId) ?? { nome: e.quadroNome, etapas: [] };
    g.etapas.push(e);
    porQuadro.set(e.quadroId, g);
  }
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium">Em qual etapa</span>
      <select
        className="campo"
        aria-label="Em qual etapa"
        value={props.valor ?? ""}
        onChange={(e) => props.aoEscolher(e.target.value || null)}
      >
        <option value="">escolha uma etapa</option>
        {Array.from(porQuadro.entries()).map(([id, g]) => (
          <optgroup key={id} label={g.nome}>
            {g.etapas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}{e.tipo !== "aberta" ? " (saída)" : ""}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <span className="text-xs text-[color:var(--tinta-fraca)]">
        O fluxo roda na conversa aberta do contato quando o card chega nesta etapa. Sem conversa aberta,
        não roda.
      </span>
    </label>
  );
}

export function MotivoDeEncerramento(props: {
  readonly valor: string | null;
  readonly aoEscolher: (v: string | null) => void;
}) {
  const q = useCatalogos();
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium">Com qual motivo</span>
      <select
        className="campo"
        value={props.valor ?? ""}
        onChange={(e) => props.aoEscolher(e.target.value || null)}
      >
        <option value="">escolha um motivo</option>
        {(q.data?.motivos ?? []).map((m) => (
          <option key={m.id} value={m.id}>
            {m.nome}
          </option>
        ))}
      </select>
      <span className="text-xs text-[color:var(--tinta-fraca)]">
        Sem motivo o fluxo não finaliza — atendimento finalizado sem motivo é uma linha que não conta em
        relatório nenhum.
      </span>
    </label>
  );
}

/**
 * O motivo de PERDA, para o passo que fecha o card.
 *
 * ⚠️ Ele é outro catálogo, e não o motivo de encerramento logo acima: encerrar a conversa e perder a
 * negociação são coisas diferentes, e o relatório do funil só lê `motivos_de_perda`. Dois seletores
 * parecidos lado a lado convidam a reusar um pelo outro — por isso a frase de baixo diz onde cada um
 * aparece.
 *
 * **Sem motivo cadastrado, ele diz onde criar** em vez de mostrar uma lista vazia: lista vazia num
 * seletor obrigatório é um beco, e a pessoa conclui que a tela está quebrada.
 */
export function MotivoDePerda(props: {
  readonly valor: string | null;
  readonly aoEscolher: (v: string | null) => void;
}) {
  const q = useMotivosDePerda();
  const itens = q.data?.itens ?? [];
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium">Com qual motivo de perda</span>
      <select
        className="campo"
        value={props.valor ?? ""}
        onChange={(e) => props.aoEscolher(e.target.value || null)}
      >
        <option value="">escolha um motivo</option>
        {itens.map((m) => (
          <option key={m.id} value={m.id}>
            {m.nome}
          </option>
        ))}
      </select>
      <span className="text-xs text-[color:var(--tinta-fraca)]">
        {q.isSuccess && itens.length === 0
          ? "Nenhum motivo de perda cadastrado. Crie um no Funil, em Etapas e motivos."
          : "É o que aparece no relatório de perdas. Sem ele o card não fecha."}
      </span>
    </label>
  );
}

/**
 * Teste ao lado (§3.4) — e ele testa o RASCUNHO, não o publicado.
 *
 * Testar o que já está no ar é testar o que não se está editando. E ele roda **a mesma `proximoPasso`** da
 * execução: um simulador com lógica própria concorda com o editor e discorda do produto, que é a pior falha
 * possível num teste.
 */
function Teste(props: { readonly passos: readonly PassoCompleto[]; readonly repeticoes: number }) {
  /*
    Fluxo de BLOCOS é testado pelo simulador de blocos, e não por este.

    Este painel roda `proximoPasso`, que é o motor do MENU. Rodá-lo sobre um passo `condicional` faria o
    simulador repetir para o contato um texto que não é pergunta, e concordar com o editor enquanto discorda do
    produto — a pior falha possível num teste. Os dois usam a função de verdade; são funções diferentes porque
    são motores diferentes.
  */
  if (usaBlocos(props.passos)) return <TesteDeBlocos passos={props.passos} />;
  return <TesteDeMenu passos={props.passos} repeticoes={props.repeticoes} />;
}

/**
 * O simulador do motor de BLOCOS.
 *
 * Roda `avaliarBloco` — a MESMA função que o servidor executa — e anda igual ao motor: em laço, até precisar de
 * algo do mundo. O que ele dubla é só a borda: mandar mensagem vira uma bolha na tela, ação e sistema externo
 * viram uma linha de "o que aconteceria", e a espera não espera.
 *
 * **Ele não chama sistema externo de verdade.** Testar um fluxo não pode escrever no ERP do cliente — e um
 * simulador que faz efeito real é uma armadilha, não uma ferramenta.
 */
function TesteDeBlocos(props: { readonly passos: readonly PassoCompleto[] }) {
  const [fio, setFio] = useState<{ de: "cliente" | "fluxo" | "nota"; texto: string }[]>([]);
  const [chave, setChave] = useState<string | null>(null);
  const [variaveis, setVariaveis] = useState<Record<string, string>>({});
  const [tentativas, setTentativas] = useState<Record<string, number>>({});
  const [terminado, setTerminado] = useState<string | null>(null);
  const [entrada, setEntrada] = useState("");

  // O teto do simulador é o do motor. Um laço no fluxo tem de travar aqui do mesmo jeito que trava lá — e
  // travar sem dizer nada seria o simulador escondendo o defeito que ele existe para achar.
  const TETO = 50;

  const andar = (comecoEm: string | null, resposta: string | null, jaNoFio: typeof fio) => {
    const porChave = new Map(props.passos.map((x) => [x.chave, x]));
    const novo = [...jaNoFio];
    let atual = comecoEm ?? props.passos[0]?.chave ?? null;
    let vars = { ...variaveis };
    let tentativasSim = { ...tentativas };
    let entradaDoPasso = resposta;
    let n = 0;

    while (atual) {
      if (n >= TETO) {
        novo.push({ de: "nota", texto: `Passou de ${TETO} passos — isto é um laço, e no ar a conversa iria para a fila.` });
        setTerminado("estourou o teto de passos");
        break;
      }
      const passo = porChave.get(atual);
      if (!passo) {
        novo.push({ de: "nota", texto: `O passo '${atual}' não existe.` });
        setTerminado("destino inexistente");
        break;
      }

      const d = avaliarBloco(passo, {
        variaveis: vars,
        respostaDoCliente: entradaDoPasso,
        filaId: null,
        canal: "whatsapp",
        etiquetas: [],
        tentativas: tentativasSim,
      });
      n += 1;

      if (d.acao === "mandar") {
        novo.push({ de: "fluxo", texto: d.texto });
        atual = d.proximo;
        entradaDoPasso = null;
        continue;
      }
      if (d.acao === "perguntar_e_guardar") {
        novo.push({ de: "fluxo", texto: d.texto });
        setChave(passo.chave);
        setVariaveis(vars);
        setFio(novo);
        return;
      }
      if (d.acao === "ir_para") {
        if ((passo.tipo ?? "menu") === "perguntar" && passo.guardaEm && entradaDoPasso) {
          vars = { ...vars, [passo.guardaEm]: entradaDoPasso };
          novo.push({ de: "nota", texto: `Guardou '${passo.guardaEm}' = “${entradaDoPasso}”.` });
        } else {
          novo.push({ de: "nota", texto: d.porque });
        }
        entradaDoPasso = null;
        atual = d.passo || null;
        if (!atual) {
          setTerminado("o passo não tinha destino");
          break;
        }
        continue;
      }
      if (d.acao === "esperar") {
        novo.push({ de: "nota", texto: `Aqui o fluxo esperaria ${d.minutos} minutos. No teste, segue direto.` });
        atual = d.proximo;
        entradaDoPasso = null;
        continue;
      }
      if (d.acao === "executar") {
        novo.push({ de: "nota", texto: `Executaria: ${d.interna.tipo}.` });
        atual = d.proximo;
        entradaDoPasso = null;
        continue;
      }
      if (d.acao === "chamar_agente") {
        // O simulador NÃO chama o modelo: custaria dinheiro por teste e daria uma resposta diferente a cada
        // execução, o que faria o mesmo fluxo passar e falhar sem ninguém mudar nada.
        novo.push({
          de: "nota",
          texto: `Chamaria o assistente com o objetivo “${d.bloco.objetivo}”, e ele responderia uma de: ${d.bloco.saidasEsperadas.join(", ")}.`,
        });
        atual = d.proximo;
        entradaDoPasso = null;
        continue;
      }
      if (d.acao === "reengajar") {
        novo.push({ de: "fluxo", texto: d.texto });
        novo.push({
          de: "nota",
          texto: `Tentativa ${d.tentativa}. Esperaria ${d.minutos} min por resposta — e responder aqui cancela o resto.`,
        });
        tentativasSim = { ...tentativasSim, [passo.chave]: d.tentativa };
        setChave(passo.chave);
        setVariaveis(vars);
        setTentativas(tentativasSim);
        setFio(novo);
        return;
      }
      if (d.acao === "chamar_ferramenta") {
        novo.push({
          de: "nota",
          texto: `Consultaria ${d.bloco.url}. No teste não chama nada — em produção, falha vai para '${d.bloco.aoFalhar ?? "—"}' e vazio para '${d.bloco.aoVirVazio ?? "—"}'.`,
        });
        atual = d.proximo;
        entradaDoPasso = null;
        continue;
      }
      if (d.acao === "terminar") {
        setTerminado(d.porque);
        break;
      }
      // `estourou`: é o que a validação já aponta, e aqui aparece com a frase do motor.
      novo.push({ de: "nota", texto: d.porque });
      setTerminado("o fluxo não pôde continuar");
      break;
    }

    setVariaveis(vars);
    setTentativas(tentativasSim);
    setFio(novo);
    setChave(null);
  };

  const reiniciar = () => {
    setTerminado(null);
    setVariaveis({});
    setTentativas({});
    andar(null, null, [{ de: "cliente", texto: "oi" }]);
  };

  const responder = () => {
    if (entrada.trim() === "" || terminado) return;
    const texto = entrada;
    setEntrada("");
    andar(chave, texto, [...fio, { de: "cliente", texto }]);
  };

  return (
    <div className="vidro flex flex-col gap-2 p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Testar</p>
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            Roda o motor de blocos de verdade. Não manda mensagem, não chama sistema externo e não chama o
            assistente — os três aparecem como nota.
          </p>
        </div>
        <Botao tipo="secundario" onClick={reiniciar}>
          {fio.length === 0 ? "Começar" : "Reiniciar"}
        </Botao>
      </div>

      {fio.length > 0 && (
        <div className="flex flex-col gap-1">
          {fio.map((m, i) =>
            m.de === "nota" ? (
              <p key={i} className="self-center text-xs italic text-[color:var(--tinta-fraca)]">
                {m.texto}
              </p>
            ) : (
              <div key={i} className={m.de === "cliente" ? "self-start" : "self-end"}>
                <div
                  className={
                    "max-w-sm whitespace-pre-wrap rounded-xl px-3 py-2 text-sm " +
                    (m.de === "cliente"
                      ? "bg-[color:var(--realce)]"
                      : "bg-[color:var(--acao-forte)] text-[color:var(--tinta-na-acao)]")
                  }
                >
                  {m.texto}
                </div>
              </div>
            ),
          )}
        </div>
      )}

      {Object.keys(variaveis).length > 0 && (
        <p className="text-xs text-[color:var(--tinta-fraca)]">
          <strong>Coletado até aqui:</strong>{" "}
          {Object.entries(variaveis)
            .map(([k, v]) => `${k} = ${v}`)
            .join(" · ")}
        </p>
      )}

      {terminado && (
        <p className="text-xs text-[color:var(--tinta-fraca)]">
          <strong>Terminou:</strong> {terminado}
        </p>
      )}

      {fio.length > 0 && !terminado && chave && (
        <div className="flex gap-2">
          <input
            className="campo flex-1"
            value={entrada}
            onChange={(e) => setEntrada(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                responder();
              }
            }}
            placeholder="Responda como o contato responderia"
          />
          <Botao onClick={responder} desabilitado={entrada.trim() === ""}>
            Responder
          </Botao>
        </div>
      )}

      {fio.length > 0 && !terminado && !chave && (
        <p className="text-xs text-[color:var(--tinta-fraca)]">
          O fluxo parou sem esperar resposta. Reinicie para rodar de novo.
        </p>
      )}
    </div>
  );
}

function TesteDeMenu(props: { readonly passos: readonly PassoDoFluxo[]; readonly repeticoes: number }) {
  const [conversa, setConversa] = useState<{ de: "cliente" | "menu"; texto: string }[]>([]);
  const [passoAtual, setPassoAtual] = useState<string | null>(null);
  const [reps, setReps] = useState(0);
  const [terminado, setTerminado] = useState<string | null>(null);
  const [entrada, setEntrada] = useState("");

  const modelo: FluxoModelo = {
    id: "teste",
    nome: "teste",
    quando: "primeira_mensagem",
    passos: props.passos,
    passosSemResposta: props.repeticoes,
    filaDeDesistenciaId: null,
  };

  const reiniciar = () => {
    setConversa([]);
    setPassoAtual(null);
    setReps(0);
    setTerminado(null);
    const r = proximoPasso(modelo, null, {
      textoDoCliente: "oi",
      donoId: null,
      donoTipo: null,
      primeiraMensagem: true,
      dentroDoHorario: false,
    });
    if (r.acao === "perguntar") {
      setConversa([
        { de: "cliente", texto: "oi" },
        { de: "menu", texto: r.texto },
      ]);
      setPassoAtual(r.passo);
    }
  };

  const responder = () => {
    if (entrada.trim() === "" || terminado) return;
    const r = proximoPasso(
      modelo,
      { passoAtual, repeticoes: reps, terminado: false },
      {
        textoDoCliente: entrada,
        donoId: null,
        donoTipo: null,
        primeiraMensagem: false,
        dentroDoHorario: false,
      },
    );
    const nova = [...conversa, { de: "cliente" as const, texto: entrada }];
    setEntrada("");

    if (r.acao === "perguntar") {
      setConversa([...nova, { de: "menu", texto: r.texto }]);
      setPassoAtual(r.passo);
      setReps(r.repeticoes);
      return;
    }
    if (r.acao === "encaminhar") {
      setConversa(nova);
      setTerminado(r.porque);
      return;
    }
    setConversa(nova);
    setTerminado(r.porque);
  };

  return (
    <div className="vidro flex flex-col gap-2 p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Testar</p>
          <p className="text-xs text-[color:var(--tinta-fraca)]">
            Testa o que está na tela, não o que está no ar. Nada sai para ninguém.
          </p>
        </div>
        <Botao tipo="secundario" onClick={reiniciar}>
          {conversa.length === 0 ? "Começar" : "Reiniciar"}
        </Botao>
      </div>

      {conversa.length > 0 && (
        <div className="flex flex-col gap-1">
          {conversa.map((m, i) => (
            <div key={i} className={m.de === "cliente" ? "self-start" : "self-end"}>
              <div
                className={
                  "max-w-sm whitespace-pre-wrap rounded-xl px-3 py-2 text-sm " +
                  (m.de === "cliente"
                    ? "bg-[color:var(--realce)]"
                    : "bg-[color:var(--acao-forte)] text-[color:var(--tinta-na-acao)]")
                }
              >
                {m.texto}
              </div>
            </div>
          ))}
        </div>
      )}

      {terminado && (
        <p className="text-xs text-[color:var(--tinta-fraca)]">
          <strong>Saiu do menu:</strong> {terminado}
        </p>
      )}

      {conversa.length > 0 && !terminado && (
        <div className="flex gap-2">
          <input
            className="campo flex-1"
            value={entrada}
            onChange={(e) => setEntrada(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                responder();
              }
            }}
            placeholder="Responda como o contato responderia"
          />
          <Botao onClick={responder} desabilitado={entrada.trim() === ""}>
            Responder
          </Botao>
        </div>
      )}
    </div>
  );
}

/** Um segmento do "Ver como" — três vistas dos MESMOS passos, e não três abas. */
function Degrauzinho(props: {
  readonly ativa: boolean;
  readonly desabilitada?: boolean;
  readonly motivo?: string | null;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.desabilitada}
      aria-pressed={props.ativa}
      {...(props.desabilitada && props.motivo ? { title: props.motivo } : {})}
      className={
        "rounded-[8px] px-2.5 py-1 text-xs transition-colors " +
        (props.ativa
          ? "bg-[color:var(--acao-clara)] font-semibold text-[color:var(--acao-texto)]"
          : props.desabilitada
            ? "cursor-not-allowed text-[color:var(--tinta-fraca)]"
            : "text-[color:var(--tinta-fraca)] hover:bg-[color:var(--vidro-controle)]")
      }
    >
      {props.children}
    </button>
  );
}

/**
 * Os quatro campos, montados em passos, **na tela**.
 *
 * Duplica `daFormaSimples` do servidor? Não: **chama a mesma função**. Ela vive no módulo puro justamente
 * para isto — a tela precisa validar o que o degrau 1 vai produzir **antes** de mandar, e ir ao servidor só
 * para saber se tem problema seria uma viagem por tecla.
 */
function passosDoSimples(f: FormaSimples): readonly PassoDoFluxo[] {
  return daFormaSimples(f);
}

/**
 * Por onde começar (§4 e §7 da spec 12).
 *
 * ## Três portas, e nenhuma é a tela em branco
 *
 * A tela em branco é onde a automação morre: a pessoa abre, não sabe o que escrever, fecha, e liga para o
 * suporte. Daí em diante o recurso existe e não é usado.
 *
 * **A entrevista vem primeiro** porque é a porta mais barata: descrever o próprio atendimento em português é
 * a coisa mais fácil que se pode pedir a alguém que não sabe o que é um nó.
 */
function Comecar(props: {
  readonly escolhido: Comeco;
  readonly aoEscolher: (c: Comeco) => void;
  readonly filas: readonly { id: string; nome: string }[];
  readonly aoTerminarEntrevista: (f: FormaSimples) => void;
}) {
  if (props.escolhido === "entrevista") {
    return <Entrevista filas={props.filas} aoTerminar={props.aoTerminarEntrevista} aoDesistir={() => props.aoEscolher("escolher")} />;
  }

  /*
    UM CATÁLOGO DE MODELOS SÓ (spec 23): os modelos moram em Automações (os ladrilhos e "Ver todos os modelos").
    Esta era a terceira porta, com outra lista de modelos — a mesma pergunta respondida em três lugares.
  */
  return (
    <div className="vidro flex flex-col gap-3 p-4">
      <div>
        <p className="text-sm font-medium">Ainda não está no ar. Quer ajuda para montar?</p>
        <p className="mt-0.5 text-xs text-[color:var(--tinta-fraca)]">Monta um rascunho para você conferir — nada é publicado.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Botao tipo="secundario" onClick={() => props.aoEscolher("entrevista")}>Me faça algumas perguntas</Botao>
      </div>
    </div>
  );
}

/**
 * A entrevista (§7.1 da spec 12) — uma pergunta de cada vez.
 *
 * ## Ela não usa provedor de linguagem, e isso é uma escolha
 *
 * As quatro perguntas do §7.1 são **fixas**: são as que ninguém lembra de responder sozinho. O valor está na
 * ordem, não em adivinhar — uma caixa de texto única devolve fluxo com buraco, porque a pessoa descreve o
 * caso feliz e esquece o resto.
 *
 * O copiloto generativo do §7 continua na fila e depende da chave do provedor. Esta parte entrega o que
 * resolve, hoje, sem depender de nada.
 *
 * ## E ela NÃO preenche o que não sabe (regra 5 do §7.2)
 *
 * Assunto sem fila escolhida sai **sem fila**, e a validação marca. Escolher "a primeira fila" seria o
 * palpite plausível — e errar de um jeito verossímil é o único modo de falha que passa despercebido.
 */
function Entrevista(props: {
  readonly filas: readonly { id: string; nome: string }[];
  readonly aoTerminar: (f: FormaSimples) => void;
  readonly aoDesistir: () => void;
}) {
  const [etapa, setEtapa] = useState(0);
  const [cumprimento, setCumprimento] = useState("");
  const [pergunta, setPergunta] = useState("Como podemos ajudar?");
  const [assuntos, setAssuntos] = useState<{ rotulo: string; filaId: string | null }[]>([
    { rotulo: "", filaId: null },
    { rotulo: "", filaId: null },
  ]);

  const perguntas = [
    {
      titulo: "Como você cumprimenta quem escreve?",
      porque: "É a primeira frase que o seu cliente lê.",
      corpo: (
        <input
          className="campo"
          autoFocus
          value={cumprimento}
          onChange={(e) => setCumprimento(e.target.value)}
          placeholder="Oi! Aqui é a Clínica Exemplo."
        />
      ),
      podeSeguir: cumprimento.trim() !== "",
    },
    {
      titulo: "E o que você pergunta em seguida?",
      porque: "Uma pergunta só. As opções vêm na próxima.",
      corpo: (
        <input
          className="campo"
          value={pergunta}
          onChange={(e) => setPergunta(e.target.value)}
          placeholder="Como podemos ajudar?"
        />
      ),
      podeSeguir: pergunta.trim() !== "",
    },
    {
      titulo: "Quais são os assuntos, e para onde vai cada um?",
      porque:
        "Se você não souber a fila agora, deixe em branco — vamos marcar como pendente em vez de escolher " +
        "uma por você.",
      corpo: (
        <div className="flex flex-col gap-2">
          {assuntos.map((a, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <span className="w-4 text-xs text-[color:var(--tinta-fraca)]">{i + 1}</span>
              <input
                className="campo min-w-40 flex-1"
                value={a.rotulo}
                onChange={(e) =>
                  setAssuntos(assuntos.map((x, j) => (j === i ? { ...x, rotulo: e.target.value } : x)))
                }
                placeholder={i === 0 ? "Agendamento" : "Financeiro"}
              />
              <select
                className="campo w-40"
                value={a.filaId ?? ""}
                onChange={(e) =>
                  setAssuntos(
                    assuntos.map((x, j) => (j === i ? { ...x, filaId: e.target.value || null } : x)),
                  )
                }
              >
                <option value="">não sei ainda</option>
                {props.filas.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <button
            type="button"
            className="self-start text-xs underline"
            onClick={() => setAssuntos([...assuntos, { rotulo: "", filaId: null }])}
          >
            + assunto
          </button>
        </div>
      ),
      podeSeguir: assuntos.some((a) => a.rotulo.trim() !== ""),
    },
  ];

  const atual = perguntas[etapa]!;
  const ultima = etapa === perguntas.length - 1;

  return (
    <div className="vidro flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{atual.titulo}</p>
          <p className="mt-0.5 text-xs text-[color:var(--tinta-fraca)]">{atual.porque}</p>
        </div>
        <span className="shrink-0 text-xs text-[color:var(--tinta-fraca)]">
          {etapa + 1} de {perguntas.length}
        </span>
      </div>

      {atual.corpo}

      <div className="flex flex-wrap items-center gap-2">
        {etapa > 0 && (
          <Botao tipo="secundario" onClick={() => setEtapa(etapa - 1)}>
            Voltar
          </Botao>
        )}
        <Botao
          onClick={() => {
            if (!ultima) {
              setEtapa(etapa + 1);
              return;
            }
            props.aoTerminar(
              daEntrevista({ comoCumprimenta: cumprimento, oQuePergunta: pergunta, assuntos, valeSempre: true }),
            );
          }}
          desabilitado={!atual.podeSeguir}
          {...(atual.podeSeguir ? {} : { motivoDeDesabilitar: "Responda para seguir." })}
        >
          {ultima ? "Montar o rascunho" : "Seguir"}
        </Botao>
        <button type="button" className="text-xs underline text-[color:var(--tinta-fraca)]" onClick={props.aoDesistir}>
          deixar para depois
        </button>
      </div>
    </div>
  );
}

/** Tamanho da carta e do respiro, em pixels. Constantes nomeadas porque o SVG precisa dos dois. */
const CARTA = { largura: 200, altura: 96, vaoX: 72, vaoY: 24 };

/**
 * O degrau 3 — o desenho (§5 da spec 12), **sem biblioteca de canvas**.
 *
 * ## Por que não há nada para arrastar
 *
 * O modelo só aponta para a frente, então o fluxo é uma árvore e o layout é canônico: `desenhar()` calcula
 * coluna e linha de cada passo, e dois fluxos iguais desenham igual sempre. Arrastar guardaria preferência
 * de layout como se fosse dado do fluxo.
 *
 * E o custo declarado do §8 desaparece com isso: nenhuma dependência nova, nada carregado sob demanda,
 * nenhuma cor fora da palheta. As guardas do §5.2 que dependiam de "a ligação não conecta" ficam de graça —
 * não há ligação para desenhar à mão.
 *
 * ## A análise mora AQUI (§5.4)
 *
 * A queda entre etapas aparece **em cima do nó** onde ela aconteceu. Relatório numa tela e desenho em outra
 * obriga a pessoa a correlacionar de cabeça; junto, ela vê onde param e clica ali.
 */
function Desenho(props: {
  readonly passos: readonly PassoDoFluxo[];
  readonly filas: readonly { id: string; nome: string }[];
  readonly uso: RespostaDoFluxo["uso"];
  readonly aoClicarNoPasso: (chave: string) => void;
}) {
  const d = useMemo(() => desenhar(props.passos), [props.passos]);

  if (d.nos.length === 0) {
    return (
      <div className="vidro p-6 text-center text-sm text-[color:var(--tinta-fraca)]">
        Não há passo para desenhar ainda.
      </div>
    );
  }

  /*
    ## Fluxo de blocos desenha DE CIMA PARA BAIXO, e o motivo é a forma dele

    O menu é largo e raso: uma pergunta, quatro opções, cada opção uma coluna. Da-e-cabe na horizontal.

    Bloco é o contrário — é uma corrente longa. O fluxo semeado aqui tem 14 passos em cadeia, e na horizontal
    isso dá 14 colunas: a tela mostra três, o resto sai pela direita, e as onze linhas de altura ficam vazias.
    Apareceu ao capturar a tela, não em typecheck.
    
    Então a profundidade vira **eixo Y** para blocos, e o scroll fica vertical — que é a direção em que se
    rola sem pensar. O cálculo é o mesmo `desenhar()`; o que muda é qual eixo recebe qual número.
  */
  const emPe = usaBlocos(props.passos);
  const passoX = CARTA.largura + CARTA.vaoX;
  const passoY = CARTA.altura + CARTA.vaoY;
  const largura = (emPe ? d.linhas : d.colunas) * passoX;
  const altura = (emPe ? d.colunas : d.linhas) * passoY;
  const x = (coluna: number) => coluna * passoX;
  const y = (linha: number) => linha * passoY;

  /*
    Quantas conversas estão em cada passo AGORA. É a análise do §5.4 sobre o desenho.
    Vem de `uso.ultimos`, que já traz `passoAtual` — não precisou de consulta nova.
  */
  const noPasso = new Map<string, number>();
  for (const u of props.uso.ultimos) {
    if (u.terminado || !u.passoAtual) continue;
    noPasso.set(u.passoAtual, (noPasso.get(u.passoAtual) ?? 0) + 1);
  }

  const nomeDaFila = (id: string | null) =>
    id ? (props.filas.find((f) => f.id === id)?.nome ?? "fila removida") : "fila padrão";

  return (
    <div className="vidro flex flex-col gap-2 p-4">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium">O desenho</p>
        <p className="text-xs text-[color:var(--tinta-fraca)]">
          {d.nos.length} de 20 passos · o layout vem do fluxo, não há o que arrastar
        </p>
      </div>

      {/* Rola na horizontal dentro do próprio cartão — fluxo largo não empurra a página. */}
      <div className="overflow-x-auto">
        <svg
          width={largura}
          height={altura}
          viewBox={`0 0 ${largura} ${altura}`}
          role="img"
          aria-label="Desenho do fluxo"
        >
          {/* As ligações primeiro, para ficarem atrás das cartas. */}
          {d.ligacoes.map((l, i) => {
            const de = d.nos.find((n) => n.chave === l.de);
            const para = d.nos.find((n) => n.chave === l.para);
            if (!de || !para) return null;
            /*
              Em pé, a seta sai de BAIXO da carta e entra em CIMA da seguinte; deitado, sai da direita e entra
              na esquerda. A curva em S troca de eixo com ela — uma curva horizontal num layout vertical
              passaria por dentro das cartas.
            */
            const x1 = emPe ? x(de.linha) + CARTA.largura / 2 : x(de.coluna) + CARTA.largura;
            const y1 = emPe ? y(de.coluna) + CARTA.altura : y(de.linha) + CARTA.altura / 2;
            const x2 = emPe ? x(para.linha) + CARTA.largura / 2 : x(para.coluna);
            const y2 = emPe ? y(para.coluna) : y(para.linha) + CARTA.altura / 2;
            const meio = emPe ? (y1 + y2) / 2 : (x1 + x2) / 2;
            return (
              <g key={`l-${i}`}>
                {/* Curva em S: reta cruzaria as cartas quando a linha de destino é distante. */}
                <path
                  d={
                    emPe
                      ? `M ${x1} ${y1} C ${x1} ${meio}, ${x2} ${meio}, ${x2} ${y2}`
                      : `M ${x1} ${y1} C ${meio} ${y1}, ${meio} ${y2}, ${x2} ${y2}`
                  }
                  fill="none"
                  stroke="var(--borda-forte)"
                  strokeWidth={1.5}
                />
                <circle cx={x2} cy={y2} r={3} fill="var(--borda-forte)" />
                <text
                  x={emPe ? (x1 + x2) / 2 + 6 : meio}
                  y={emPe ? meio : (y1 + y2) / 2 - 4}
                  textAnchor={emPe ? "start" : "middle"}
                  className="text-[10px]"
                  fill="var(--tinta-fraca)"
                >
                  {l.rotulo}
                </text>
              </g>
            );
          })}

          {/* As saídas para fila: mesma coisa, e a ponta solta fica em âmbar (§5.2). */}
          {d.saidas.map((s, i) => {
            const de = d.nos.find((n) => n.chave === s.de);
            if (!de) return null;
            const x1 = x(de.coluna) + CARTA.largura;
            const y1 = y(de.linha) + CARTA.altura / 2;
            const x2 = x(s.coluna);
            const y2 = y(s.linha) + CARTA.altura / 2;
            const meio = (x1 + x2) / 2;
            return (
              <g key={`s-${i}`}>
                <path
                  d={`M ${x1} ${y1} C ${meio} ${y1}, ${meio} ${y2}, ${x2} ${y2}`}
                  fill="none"
                  stroke={s.solta ? "var(--atencao-texto)" : "var(--ok-texto)"}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                />
                <text x={meio} y={(y1 + y2) / 2 - 4} textAnchor="middle" className="text-[10px]" fill="var(--tinta-fraca)">
                  {s.rotulo}
                </text>
                <rect
                  x={x2}
                  y={y2 - 14}
                  width={CARTA.largura}
                  height={28}
                  rx={14}
                  fill={s.solta ? "var(--atencao-clara)" : "var(--ok-clara)"}
                />
                <text x={x2 + 12} y={y2 + 4} className="text-[11px]" fill="var(--tinta-forte)">
                  → {nomeDaFila(s.filaId)}
                </text>
              </g>
            );
          })}

          {/* As cartas. Clicar leva ao passo na lista — o desenho não edita, ele navega. */}
          {d.nos.map((n) => {
            const emCurso = noPasso.get(n.chave) ?? 0;
            return (
              <g
                key={n.chave}
                onClick={() => props.aoClicarNoPasso(n.chave)}
                className="cursor-pointer"
              >
                <rect
                  x={emPe ? x(n.linha) : x(n.coluna)}
                  y={emPe ? y(n.coluna) : y(n.linha)}
                  width={CARTA.largura}
                  height={CARTA.altura}
                  rx={12}
                  fill="var(--vidro-solido)"
                  stroke={n.orfao ? "var(--atencao-texto)" : "var(--borda-forte)"}
                  strokeWidth={n.orfao ? 2 : 1}
                />
                <text x={(emPe ? x(n.linha) : x(n.coluna)) + 12} y={(emPe ? y(n.coluna) : y(n.linha)) + 20} className="text-[11px] font-mono" fill="var(--tinta-fraca)">
                  {n.chave}
                  {/* O tipo na caixa: um `esperar` e um `condicional` desenhados iguais fazem o canvas
                      parecer um fluxo linear que não é. */}
                  {n.tipo ? ` · ${TIPOS.find((tt) => tt.id === n.tipo)?.rotulo ?? n.tipo}` : ""}
                </text>
                <text x={(emPe ? x(n.linha) : x(n.coluna)) + 12} y={(emPe ? y(n.coluna) : y(n.linha)) + 40} className="text-[12px]" fill="var(--tinta-forte)">
                  {n.pergunta.split("\n")[0]?.slice(0, 26) ?? ""}
                </text>
                <text x={(emPe ? x(n.linha) : x(n.coluna)) + 12} y={(emPe ? y(n.coluna) : y(n.linha)) + 60} className="text-[10px]" fill="var(--tinta-fraca)">
                  {n.quantasOpcoes}{" "}
                  {n.tipo
                    ? n.quantasOpcoes === 1
                      ? "saída"
                      : "saídas"
                    : n.quantasOpcoes === 1
                      ? "resposta"
                      : "respostas"}
                  {n.orfao ? " · ninguém chega aqui" : ""}
                </text>
                {/* A ANÁLISE sobre o desenho (§5.4): quantas conversas estão paradas neste passo agora. */}
                {emCurso > 0 && (
                  <>
                    <circle cx={(emPe ? x(n.linha) : x(n.coluna)) + CARTA.largura - 18} cy={(emPe ? y(n.coluna) : y(n.linha)) + 18} r={12} fill="var(--acao-forte)" />
                    <text
                      x={(emPe ? x(n.linha) : x(n.coluna)) + CARTA.largura - 18}
                      y={(emPe ? y(n.coluna) : y(n.linha)) + 22}
                      textAnchor="middle"
                      className="text-[11px]"
                      fill="var(--tinta-na-acao)"
                    >
                      {emCurso}
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      <p className="text-xs text-[color:var(--tinta-fraca)]">
        {emPe
          ? "A seta diz por que ela existe: “quando vale”, “quando não vale”, “falhou”, “veio vazio”. Âmbar é passo que ninguém alcança. O número azul é quantas conversas estão paradas naquele passo agora. Clique numa carta para editá-la."
          : "Linha cheia leva a outra pergunta; tracejada encaminha para uma fila. Âmbar é o que precisa de atenção — passo que ninguém alcança, ou resposta sem fila escolhida. O número azul é quantas conversas estão paradas naquele passo agora. Clique numa carta para editá-la."}
      </p>
    </div>
  );
}
